import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Users, UsersRound, TrendingUp, Target, Award, Download, ChevronDown, ChevronUp, ChevronsUpDown, Filter, Calendar, ArrowUpRight, ArrowDownRight, Minus, Zap, MessageCircle, Send, ThumbsUp, AlertTriangle, TrendingDown, Clock, CheckCircle, Star, Mail, Video, Sparkles, BarChart3, Home, UserCog, FileText, Bell, X, RefreshCw, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, Legend, ComposedChart } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { AIInsightPopover } from './AIInsightPopover';
import { ResetFiltersButton } from './ResetFiltersButton';
import { getSkillPerformanceMetrics } from '../utils/skillPerformance';
// Mock data for demonstration
const mockLearners = [{
  id: 1,
  name: 'Linda Taylor',
  email: 'linda.taylor@example.com',
  assessments: 3,
  avgScore: 85,
  lastAttempt: '2024-01-15'
}, {
  id: 2,
  name: 'John Smith',
  email: 'john.smith@example.com',
  assessments: 2,
  avgScore: 72,
  lastAttempt: '2024-01-14'
}, {
  id: 3,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  assessments: 4,
  avgScore: 91,
  lastAttempt: '2024-01-16'
}, {
  id: 4,
  name: 'Michael Chen',
  email: 'mchen@example.com',
  assessments: 3,
  avgScore: 78,
  lastAttempt: '2024-01-13'
}, {
  id: 5,
  name: 'Emily Davis',
  email: 'emily.d@example.com',
  assessments: 2,
  avgScore: 88,
  lastAttempt: '2024-01-15'
}];
const moduleData = [{
  module: 'HTML Basics',
  mastery: 85,
  misinformation: 15,
  avgScore: 82,
  attempts: 45,
  confidentCorrect: 80,
  uncertainCorrect: 8,
  confidentIncorrect: 5,
  uncertainIncorrect: 7
}, {
  module: 'CSS Fundamentals',
  mastery: 78,
  misinformation: 22,
  avgScore: 76,
  attempts: 42,
  confidentCorrect: 75,
  uncertainCorrect: 10,
  confidentIncorrect: 8,
  uncertainIncorrect: 7
}, {
  module: 'JavaScript Basics',
  mastery: 72,
  misinformation: 28,
  avgScore: 71,
  attempts: 38,
  confidentCorrect: 65,
  uncertainCorrect: 10,
  confidentIncorrect: 15,
  uncertainIncorrect: 10
}, {
  module: 'Responsive Design',
  mastery: 80,
  misinformation: 20,
  avgScore: 79,
  attempts: 35,
  confidentCorrect: 78,
  uncertainCorrect: 10,
  confidentIncorrect: 6,
  uncertainIncorrect: 6
}];
const scoreDistribution = [{
  range: '0-20%',
  count: 2
}, {
  range: '21-40%',
  count: 5
}, {
  range: '41-60%',
  count: 8
}, {
  range: '61-80%',
  count: 15
}, {
  range: '81-100%',
  count: 12
}];
const attemptTrends = [{
  attempt: '1st',
  avgScore: 68
}, {
  attempt: '2nd',
  avgScore: 75
}, {
  attempt: '3rd',
  avgScore: 82
}, {
  attempt: '4th',
  avgScore: 86
}];
const individualModulePerformance = [{
  module: 'HTML Basics',
  score: 92,
  mastery: 95,
  misinformation: 5,
  attempts: 2,
  // Confidence breakdown data
  confidentCorrect: 80,
  uncertainCorrect: 8,
  confidentIncorrect: 5,
  uncertainIncorrect: 7
}, {
  module: 'CSS Fundamentals',
  score: 85,
  mastery: 88,
  misinformation: 12,
  attempts: 1,
  confidentCorrect: 75,
  uncertainCorrect: 10,
  confidentIncorrect: 8,
  uncertainIncorrect: 7
}, {
  module: 'JavaScript Basics',
  score: 78,
  mastery: 75,
  misinformation: 25,
  attempts: 3,
  confidentCorrect: 65,
  uncertainCorrect: 10,
  confidentIncorrect: 15,
  uncertainIncorrect: 10
}, {
  module: 'Responsive Design',
  score: 88,
  mastery: 90,
  misinformation: 10,
  attempts: 1,
  confidentCorrect: 78,
  uncertainCorrect: 10,
  confidentIncorrect: 6,
  uncertainIncorrect: 6
}];
const performanceTableData = [{
  name: 'David Hansen',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Chad Mundt',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Claudiu Budurlean',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Tiffany La Mertha',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '91.67%',
  confidentIncorrect: '0%'
}, {
  name: 'Uttam Parmar',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Jacob Zanders',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Jeevan Kandimalla',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Shannon Catt',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '100%',
  confidentIncorrect: '0%'
}, {
  name: 'Guna Bellampalli',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '83.33%',
  confidentIncorrect: '0%'
}, {
  name: 'Alexis Bensky',
  finalScore: '100%',
  improvement: '+0%',
  attempts: 1,
  confidentCorrect: '0%',
  confidentIncorrect: '0%'
}];
type PerformanceTableSortKey = 'name' | 'finalScore' | 'improvement' | 'attempts' | 'confidentCorrect' | 'confidentIncorrect';
// Action items mock data
const actionItems = [{
  id: 1,
  type: 'urgent',
  category: 'struggling',
  learner: {
    name: 'John Smith',
    email: 'john.smith@example.com',
    avatar: 'JS'
  },
  insight: 'Failed JavaScript Basics 3 times with declining scores',
  details: 'Score dropped from 68% → 65% → 58% over 3 attempts. High confident error rate (45%) on core concepts.',
  module: 'JavaScript Basics',
  lastActivity: '2 days ago',
  priority: 'high',
  suggestedActions: ['Schedule 1-on-1 check-in', 'Recommend prerequisite review', 'Assign peer mentor']
}, {
  id: 2,
  type: 'macro',
  category: 'skill-issue',
  scope: 'system',
  insight: 'CSS Fundamentals has 68% failure rate across all learners',
  details: '42 of 62 learners (68%) are failing CSS Fundamentals on first attempt. Average score is 54%, well below the 70% passing threshold. Common struggle areas: Flexbox (82% error rate), Grid Layout (76% error rate), and Positioning (71% error rate).',
  module: 'CSS Fundamentals',
  affectedLearners: 42,
  lastActivity: 'Ongoing',
  priority: 'high',
  suggestedActions: ['Email the learners who are failing', 'Point learners to Flexbox and Grid practice', 'Schedule a group study session', 'Recommend how learners should prepare']
}, {
  id: 3,
  type: 'celebrate',
  category: 'high-performer',
  learner: {
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    avatar: 'SJ'
  },
  insight: 'Achieved 95%+ on all 4 assessments on first attempt',
  details: 'Consistent high performance with strong mastery across all modules. Completed skill 2 weeks ahead of schedule.',
  module: 'All Modules',
  lastActivity: '1 day ago',
  priority: 'positive',
  suggestedActions: ['Send congratulations', 'Invite to mentor program', 'Recommend advanced track']
}, {
  id: 4,
  type: 'macro',
  category: 'low-engagement',
  scope: 'system',
  insight: 'Responsive Design skill has only 12% enrollment rate',
  details: 'Only 30 of 248 active learners (12%) have started Responsive Design, despite it being a core requirement. Skill has been available for 45 days. Learners cite "intimidating title" and "unclear prerequisites" as barriers in feedback surveys.',
  module: 'Responsive Design',
  affectedLearners: 218,
  lastActivity: 'Last 45 days',
  priority: 'medium',
  suggestedActions: ['Send targeted enrollment emails', 'Show learners who have not started', 'Offer a short orientation session', 'Explain what to complete first']
}, {
  id: 5,
  type: 'attention',
  category: 'at-risk',
  learner: {
    name: 'Michael Chen',
    email: 'mchen@example.com',
    avatar: 'MC'
  },
  insight: 'No activity for 7 days, last score was 62%',
  details: 'Was on track but has been inactive. Last assessment showed struggle with CSS layouts.',
  module: 'CSS Fundamentals',
  lastActivity: '7 days ago',
  priority: 'medium',
  suggestedActions: ['Send check-in message', 'Point Michael to CSS layout practice', 'Extend deadline']
}, {
  id: 6,
  type: 'macro',
  category: 'content-gap',
  scope: 'system',
  insight: 'High confident error rate (34%) on JavaScript async concepts',
  details: 'Across 156 attempts on JavaScript Basics, async/await and Promises questions show a 34% confident error rate (learners confidently selecting wrong answers). This is 2.8x higher than the skill average of 12%. Learners are attempting these questions before they have practiced the concepts.',
  module: 'JavaScript Basics',
  affectedLearners: 89,
  lastActivity: 'Last 30 days',
  priority: 'high',
  suggestedActions: ['Show the 89 learners affected', 'Draft a study nudge for these learners', 'Suggest what learners should review first', 'Schedule an async/await study session']
}, {
  id: 7,
  type: 'celebrate',
  category: 'improvement',
  learner: {
    name: 'Emily Davis',
    email: 'emily.d@example.com',
    avatar: 'ED'
  },
  insight: 'Improved from 65% to 88% after focused practice',
  details: 'Showed significant improvement on HTML Basics retake. Mastery increased from 60% to 92%.',
  module: 'HTML Basics',
  lastActivity: '1 day ago',
  priority: 'positive',
  suggestedActions: ['Send encouragement', 'Highlight progress', 'Share success story']
}, {
  id: 8,
  type: 'macro',
  category: 'success-pattern',
  scope: 'system',
  insight: 'HTML Basics pass rate is 23% higher where learners used the videos',
  details: 'Learners who watched the supplemental video tutorials passed HTML Basics on the first attempt 87% of the time, compared with 64% for those who did not. Average attempts dropped from 4.2 to 3.1. Learner feedback mentions "videos made concepts click" (mentioned in 78% of positive reviews).',
  module: 'HTML Basics',
  affectedLearners: 124,
  lastActivity: 'Last 3 weeks',
  priority: 'positive',
  suggestedActions: ['Point other learners to the video resources', 'Recognize the learners who improved most', 'Share what worked with struggling learners', 'Show which learners have not used the videos']
}, {
  id: 9,
  type: 'urgent',
  category: 'struggling',
  learner: {
    name: 'Alex Rivera',
    email: 'alex.r@example.com',
    avatar: 'AR'
  },
  insight: 'Consistently scoring below 50% across all modules',
  details: 'Attempted 5 assessments with average score of 45%. A high confident error rate suggests fundamental gaps.',
  module: 'Multiple Modules',
  lastActivity: '3 days ago',
  priority: 'high',
  suggestedActions: ['Schedule urgent meeting', 'Identify what is blocking Alex', 'Build a personal study plan']
}, {
  id: 10,
  type: 'macro',
  category: 'time-pattern',
  scope: 'system',
  insight: 'Weekend attempts have 41% higher failure rate than weekday',
  details: 'Analysis of 892 assessment attempts shows weekend attempts (Sat-Sun) have 41% higher failure rate (58% vs 41%). Possible causes: less instructor support available, learners rushing through content, or fatigue from weekday work. Pattern consistent across all skills.',
  module: 'All Skills',
  affectedLearners: 187,
  lastActivity: 'Last 60 days',
  priority: 'medium',
  suggestedActions: ['Send "take your time" reminders on Fridays', 'Show learners who attempt on weekends', 'Offer weekend check-in support', 'Encourage learners to attempt when support is available']
}, {
  id: 11,
  type: 'attention',
  category: 'engagement',
  learner: {
    name: 'Lisa Wong',
    email: 'lisa.w@example.com',
    avatar: 'LW'
  },
  insight: 'Taking unusually long between attempts (10+ days)',
  details: 'Scores are adequate (75% avg) but engagement pattern suggests time management issues.',
  module: 'Responsive Design',
  lastActivity: '5 days ago',
  priority: 'medium',
  suggestedActions: ['Check on progress', 'Offer time management tips', 'Discuss barriers']
}];
export function AnalyticsDashboard() {
  const [selectedLearner, setSelectedLearner] = useState<number | null>(null);
  const [isLearnerDropdownOpen, setIsLearnerDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<string>('All Skills Combined');
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState<string>('Score Distribution');
  const [isChartTypeDropdownOpen, setIsChartTypeDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'assessment' | 'progress'>('assessment');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const skillDropdownRef = useRef<HTMLDivElement>(null);
  const learnerDropdownRef = useRef<HTMLDivElement>(null);
  const chartTypeDropdownRef = useRef<HTMLDivElement>(null);
  // Let the Phoebe walkthrough drive which analytics tab is showing
  useEffect(() => {
    const handleTourTab = (event: Event) => {
      const requestedTab = (event as CustomEvent<'assessment' | 'progress'>).detail;
      if (requestedTab === 'assessment' || requestedTab === 'progress') {
        setActiveTab(requestedTab);
      }
    };
    window.addEventListener('phoebe:analytics-tab', handleTourTab);
    return () => window.removeEventListener('phoebe:analytics-tab', handleTourTab);
  }, []);
  const skillOptions = ['All Skills Combined', 'HTML Basics', 'CSS Fundamentals', 'JavaScript Basics', 'Responsive Design'];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
      if (learnerDropdownRef.current && !learnerDropdownRef.current.contains(event.target as Node)) {
        setIsLearnerDropdownOpen(false);
      }
      if (chartTypeDropdownRef.current && !chartTypeDropdownRef.current.contains(event.target as Node)) {
        setIsChartTypeDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isSkillDropdownOpen || isLearnerDropdownOpen || isChartTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isSkillDropdownOpen, isLearnerDropdownOpen, isChartTypeDropdownOpen]);
  const totalLearners = mockLearners.length;
  const totalAssessments = mockLearners.reduce((sum, l) => sum + l.assessments, 0);
  const overallAvgScore = Math.round(mockLearners.reduce((sum, l) => sum + l.avgScore, 0) / totalLearners);
  const overallMastery = Math.round(moduleData.reduce((sum, m) => sum + m.mastery, 0) / moduleData.length);
  // Derive viewMode from state
  const viewMode = selectedLearner ? 'individual' : 'aggregate';
  const selectedLearnerData = selectedLearner ? mockLearners.find(l => l.id === selectedLearner) : null;
  const COLORS = ['#30718D', '#007558', '#FBBC04', '#DB3725'];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-256" data-mp="1qy0xaapcy88z">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-257" data-mp="1qw6ccwe36g4w">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258" data-mp="1vxvqikwlv9x7">
          <div className="flex items-center h-16" data-id="element-259" data-mp="261ka018nyp9o">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-260" data-mp="211xv7nd6t0yr">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-261" data-mp="1w2l8d2mbftw6">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-262" data-mp="1w4o9oisx59g7">
                <Home size={18} data-id="element-263" data-mp="2jrxhy3lk23z0" />
                <span className="hidden sm:inline" data-id="element-264" data-mp="2jtqrprrgt4b8">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-265" data-mp="2f0nun9nhbcqa">
                <BarChart3 size={18} data-id="element-266" data-mp="32rjqqiyywu3c" />
                <span className="hidden sm:inline" data-id="element-267" data-mp="32qi5jlo8mr7w">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-268" data-mp="3meanplbwntev">
                <FileText size={18} data-id="element-269" data-mp="3cf1l642z56gw" />
                <span className="hidden sm:inline" data-id="element-270" data-mp="3hfzbj3y516th">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-271" data-mp="90fnqkg8kglt">
                <UserCog size={18} data-id="element-272" data-mp="3v6m38c5hdwg2" />
                <span className="hidden sm:inline" data-id="element-273" data-mp="3v9qbbkm2hcc0">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-274" data-mp="2jryz4bfhio7j">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-275" data-mp="2ork0esi4n8nf">
                <Bell size={20} data-id="element-276" data-mp="11tsows74xfvp" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-277" data-mp="11wycu4412bpu"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-278" data-mp="1bt1pi3wi9sr1">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-279" data-mp="1btjimrnvfquq">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-280" data-mp="j29mamh0w3u7">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-281" data-mp="s3hfiq4omehf">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-282" data-mp="e4jpic3qowz" />
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-283" data-mp="2qiqgwgoxo8dr">
                    <div className="py-2" data-id="element-284" data-mp="30bfrzagknk8k">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-285" data-mp="30c7b3lszfphy">
                        <Users size={18} className="text-gray-600" data-id="element-286" data-mp="3aawwhok37s2y" />
                        <span className="font-medium text-gray-900" data-id="element-287" data-mp="359h7jlesh1fa">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-288" data-mp="3ea5qmuj1hrz1">
                        <Home size={18} className="text-gray-600" data-id="element-289" data-mp="1ow95jrx0jllh" />
                        <span className="font-medium text-gray-900" data-id="element-290" data-mp="1jxfvk9gxqcq2">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-291" data-mp="4dowebqh3oe3">
                        <UserCog size={18} className="text-gray-600" data-id="element-292" data-mp="3mqmuse7ma965" />
                        <span className="font-medium text-gray-900" data-id="element-293" data-mp="3qt186isiogq1">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-294" data-mp="3cvl43dxp0z1g"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-295" data-mp="3hu65hgoolorh">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-296" data-mp="37uf9fakh31pa">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-297" data-mp="2a04fjwaxtpzn"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-298" data-mp="260c7dkpghlut"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-299" data-mp="210hghh5okw9r"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-300" data-mp="2xq8l497orzta">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div data-phoebe-tour="analytics" className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-4" data-id="element-301" data-mp="2jy0smtnfqv0e">
        <div data-id="element-302" data-mp="3hf96xoj9ntgf">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-303" data-mp="3hfsi8meevjlh">
            Analytics Dashboard
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base" data-id="element-304" data-mp="3clwjviu8jups">
            Track learner progress and assessment performance
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div data-phoebe-tour="analytics-tabs" className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305" data-mp="3p032ok3xo4s9">
        <div className="border-b border-gray-200 flex items-center" data-id="element-306" data-mp="3tz69a8zkaj2c">
          <button data-phoebe-nav="analytics-tab-assessment" onClick={() => setActiveTab('assessment')} className={`relative px-4 sm:px-6 py-3 text-sm sm:text-base font-bold transition-colors ${activeTab === 'assessment' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-307" data-mp="1u2bwctr0gll">
            Assessment Analytics
            {activeTab === 'assessment' && <span className="absolute left-0 right-0 -bottom-px h-1 bg-[#DB3725]" data-id="element-308" data-mp="lgqmryxzn056"></span>}
          </button>
          <button data-phoebe-nav="analytics-tab-progress" onClick={() => setActiveTab('progress')} className={`relative px-4 sm:px-6 py-3 text-sm sm:text-base font-bold transition-colors ${activeTab === 'progress' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-309" data-mp="ph0pn2qhhaft">
            Learner Progress
            {activeTab === 'progress' && <span className="absolute left-0 right-0 -bottom-px h-1 bg-[#DB3725]" data-id="element-310" data-mp="qn5ao9zcnrf1"></span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6" data-id="element-311" data-mp="3u29kbzp5b63o">
        {activeTab === 'progress' ? <LearnerProgressTable data-id="element-312" data-mp="3q28wxpk2jkkh" /> : viewMode === 'aggregate' ? <AggregateView totalLearners={totalLearners} totalAssessments={totalAssessments} overallAvgScore={overallAvgScore} overallMastery={overallMastery} moduleData={moduleData} scoreDistribution={scoreDistribution} attemptTrends={attemptTrends} COLORS={COLORS} selectedSkill={selectedSkill} setSelectedSkill={setSelectedSkill} isSkillDropdownOpen={isSkillDropdownOpen} setIsSkillDropdownOpen={setIsSkillDropdownOpen} skillDropdownRef={skillDropdownRef} skillOptions={skillOptions} selectedLearner={selectedLearner} setSelectedLearner={setSelectedLearner} selectedLearnerData={selectedLearnerData} isLearnerDropdownOpen={isLearnerDropdownOpen} setIsLearnerDropdownOpen={setIsLearnerDropdownOpen} learnerDropdownRef={learnerDropdownRef} data-id="element-313" data-mp="7vchofka667b" /> : selectedLearnerData ? <IndividualView learner={selectedLearnerData} modulePerformance={individualModulePerformance} actionItems={actionItems} COLORS={COLORS} selectedSkill={selectedSkill} setSelectedSkill={setSelectedSkill} isSkillDropdownOpen={isSkillDropdownOpen} setIsSkillDropdownOpen={setIsSkillDropdownOpen} skillDropdownRef={skillDropdownRef} skillOptions={skillOptions} selectedLearner={selectedLearner} setSelectedLearner={setSelectedLearner} selectedLearnerData={selectedLearnerData} isLearnerDropdownOpen={isLearnerDropdownOpen} setIsLearnerDropdownOpen={setIsLearnerDropdownOpen} learnerDropdownRef={learnerDropdownRef} data-id="element-314" data-mp="364bmvr2jimch" /> : <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100" data-id="element-315" data-mp="3pro942dzmu7a">
            <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-316" data-mp="3kslgjhf72gg7" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-317" data-mp="3kqs57otmz81v">
              Select a Learner
            </h3>
            <p className="text-gray-600" data-id="element-318" data-mp="3aup5sqq65nim">
              Choose a learner from the dropdown above to view their individual
              performance
            </p>
          </div>}
      </div>
    </div>;
}
const learnerProgressData = [{
  name: 'Alexis Bensky',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 1,
  firstScore: '91.67%',
  finalScore: '91.67%',
  improvement: '+0%',
  ccFirst: '0%',
  ccFinal: '0%'
}, {
  name: 'Alexis Bensky',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '0%',
  ccFinal: '0%'
}, {
  name: 'Alexis Bensky',
  module: 'Leading with Effective Communication',
  attempts: 3,
  firstScore: '75%',
  finalScore: '100%',
  improvement: '+25%',
  ccFirst: '0%',
  ccFinal: '0%'
}, {
  name: 'Chad Mundt',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '100%',
  ccFinal: '100%'
}, {
  name: 'Claudiu Budurlean',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '100%',
  ccFinal: '100%'
}, {
  name: 'Cora Asher',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 1,
  firstScore: '75%',
  finalScore: '75%',
  improvement: '+0%',
  ccFirst: '75%',
  ccFinal: '75%'
}, {
  name: 'David Hansen',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '100%',
  ccFinal: '100%'
}, {
  name: 'Guna Bellampalli',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '83.33%',
  ccFinal: '83.33%'
}, {
  name: 'Jacob Zanders',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 2,
  firstScore: '83.33%',
  finalScore: '100%',
  improvement: '+16.67%',
  ccFirst: '83.33%',
  ccFinal: '100%'
}, {
  name: 'Jacob Zanders',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '100%',
  ccFinal: '100%'
}, {
  name: 'Jacob Zanders',
  module: 'Leading with Effective Communication',
  attempts: 3,
  firstScore: '83.33%',
  finalScore: '100%',
  improvement: '+16.67%',
  ccFirst: '83.33%',
  ccFinal: '100%'
}, {
  name: 'Maya Patel',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 2,
  firstScore: '66.67%',
  finalScore: '91.67%',
  improvement: '+25%',
  ccFirst: '50%',
  ccFinal: '83.33%'
}, {
  name: 'Maya Patel',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '100%',
  ccFinal: '100%'
}, {
  name: 'Noah Williams',
  module: 'Leading with Effective Communication',
  attempts: 2,
  firstScore: '75%',
  finalScore: '91.67%',
  improvement: '+16.67%',
  ccFirst: '66.67%',
  ccFinal: '83.33%'
}, {
  name: 'Olivia Brown',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 1,
  firstScore: '83.33%',
  finalScore: '83.33%',
  improvement: '+0%',
  ccFirst: '83.33%',
  ccFinal: '83.33%'
}, {
  name: 'Olivia Brown',
  module: 'Giving and Receiving Feedback',
  attempts: 3,
  firstScore: '58.33%',
  finalScore: '91.67%',
  improvement: '+33.34%',
  ccFirst: '41.67%',
  ccFinal: '75%'
}, {
  name: 'Priya Shah',
  module: 'Leading with Effective Communication',
  attempts: 1,
  firstScore: '100%',
  finalScore: '100%',
  improvement: '+0%',
  ccFirst: '91.67%',
  ccFinal: '91.67%'
}, {
  name: 'Quinn Rivera',
  module: 'Empathetic Communication and Emotional Management',
  attempts: 2,
  firstScore: '75%',
  finalScore: '100%',
  improvement: '+25%',
  ccFirst: '66.67%',
  ccFinal: '100%'
}, {
  name: 'Rachel Kim',
  module: 'Giving and Receiving Feedback',
  attempts: 1,
  firstScore: '91.67%',
  finalScore: '91.67%',
  improvement: '+0%',
  ccFirst: '83.33%',
  ccFinal: '83.33%'
}, {
  name: 'Samuel Okafor',
  module: 'Leading with Effective Communication',
  attempts: 3,
  firstScore: '50%',
  finalScore: '83.33%',
  improvement: '+33.33%',
  ccFirst: '33.33%',
  ccFinal: '66.67%'
}];
type LearnerProgressSortKey = 'name' | 'module' | 'attempts' | 'firstScore' | 'finalScore' | 'improvement' | 'ccFirst' | 'ccFinal';
function parsePercent(value: string | number) {
  if (typeof value === 'number') return value;
  const cleaned = value.replace('%', '').replace('+', '').trim();
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}
function LearnerProgressTable() {
  const [showDetails, setShowDetails] = useState(true);
  const [sortKey, setSortKey] = useState<LearnerProgressSortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [moduleFilter, setModuleFilter] = useState<string>('All Modules');
  const [moduleSearch, setModuleSearch] = useState<string>('');
  const [isModuleDropdownOpen, setIsModuleDropdownOpen] = useState(false);
  const [isPaginationDropdownOpen, setIsPaginationDropdownOpen] = useState(false);
  const moduleDropdownRef = useRef<HTMLDivElement>(null);
  const paginationDropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 10;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(event.target as Node)) {
        setIsModuleDropdownOpen(false);
      }
      if (paginationDropdownRef.current && !paginationDropdownRef.current.contains(event.target as Node)) {
        setIsPaginationDropdownOpen(false);
      }
    };
    if (isModuleDropdownOpen || isPaginationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModuleDropdownOpen, isPaginationDropdownOpen]);
  const handleSort = (key: LearnerProgressSortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };
  const moduleFilteredData = moduleFilter === 'All Modules' ? learnerProgressData : learnerProgressData.filter(r => r.module === moduleFilter);
  const sortedData = [...moduleFilteredData].sort((a, b) => {
    let valA: string | number = a[sortKey];
    let valB: string | number = b[sortKey];
    if (sortKey === 'attempts' || sortKey === 'firstScore' || sortKey === 'finalScore' || sortKey === 'improvement' || sortKey === 'ccFirst' || sortKey === 'ccFinal') {
      const numA = parsePercent(valA as any);
      const numB = parsePercent(valB as any);
      return sortDir === 'asc' ? numA - numB : numB - numA;
    }
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
  });
  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedData = sortedData.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage);
  const isActive = (key: LearnerProgressSortKey) => sortKey === key;
  const SortArrow = ({
    columnKey
  }: {
    columnKey: LearnerProgressSortKey;
  }) => {
    if (!isActive(columnKey)) {
      return <ChevronsUpDown size={14} className="text-gray-400" data-id="element-319" data-mp="3g2jeolf6brjr" />;
    }
    return sortDir === 'asc' ? <ChevronUp size={14} className="text-[#30718D]" data-id="element-320" data-mp="3l1m818lsk9bw" /> : <ChevronDown size={14} className="text-[#30718D]" data-id="element-321" data-mp="3diwgyx6ul11e" />;
  };
  const headerBtnClass = (columnKey: LearnerProgressSortKey) => `flex items-center gap-1 text-left text-sm font-medium transition-colors ${isActive(columnKey) ? 'text-[#30718D]' : 'text-gray-700 hover:text-gray-900'}`;
  const cellActiveClass = (columnKey: LearnerProgressSortKey) => isActive(columnKey) ? 'bg-[#30718D]/5' : '';
  const headerActiveClass = (columnKey: LearnerProgressSortKey) => isActive(columnKey) ? 'bg-[#30718D]/10' : '';
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible" data-id="element-322" data-mp="2psiy9xa5kejh">
      {/* Header */}
      <div data-phoebe-tour="learner-progress-header" className="px-4 sm:px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 overflow-visible" data-id="element-323" data-mp="2kv90nucm0wkk">
        <div data-id="element-324" data-mp="2bszoz15lux5l">
          <div className="flex items-center gap-2" data-id="element-325" data-mp="2bvlxb30blgov">
            <h4 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2" data-id="element-326" data-mp="26vr7z3w70z5x">
              <UsersRound size={22} className="text-[#30718D]" data-id="element-327" data-mp="1x611fh9vcmw5" />
              Detailed Summary Table by Learner
            </h4>
            <AIInsightPopover align="left" headline="Retakes are working — first attempts are the gap" insightBeforeMetric="Learners who retook a module improved by an average of" highlightedMetric="21 points" insightAfterMetric=", but most only pass on a later attempt." explanation="Leading with Effective Communication needs the most attempts, so earlier practice there would reduce retakes." insightId={15} data-id="element-328" data-mp="21a0f7s3rl7lt" />
          </div>
          <p className="text-sm text-gray-500 ml-[26px]" data-id="element-329" data-mp="2b6lkmejp4kq6">
            Individual learner performance metrics by skill module
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3" data-id="element-330" data-mp="2yuv2j5dsgoc1">
          {/* Skill module search dropdown */}
          <div className="relative" ref={moduleDropdownRef} data-id="element-331" data-mp="s87331sahva4">
            <button type="button" onClick={() => {
            setIsModuleDropdownOpen(!isModuleDropdownOpen);
            setModuleSearch('');
          }} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[240px]" data-id="element-332" data-mp="s6ckvv8ekh9q">
              <Target size={16} className="text-[#30718D]" data-id="element-333" data-mp="9bffwh9mmw0s" />
              <span className="flex-1 text-left truncate" data-id="element-334" data-mp="4b9uhitya5r5">{moduleFilter}</span>
              <ChevronDown size={16} className={`transition-transform text-gray-500 ${isModuleDropdownOpen ? 'rotate-180' : ''}`} data-id="element-335" data-mp="iaj0jpofzjhe" />
            </button>

            {isModuleDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full sm:w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-id="element-336" data-mp="1tnliao2a2f35">
                <div className="p-2 border-b border-gray-100" data-id="element-337" data-mp="28sg3grpz4s1a">
                  <div className="relative" data-id="element-338" data-mp="28u0y0hcagaz5">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-339" data-mp="2dqrcj08g2yig" />
                    <input type="text" autoFocus value={moduleSearch} onChange={e => setModuleSearch(e.target.value)} placeholder="Search skill modules..." className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D]/40 focus:border-[#30718D]" data-id="element-340" data-mp="2mjcjzk6mlirz" />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto py-1" data-id="element-341" data-mp="173ca19dje7cj">
                  {(() => {
                const uniqueModules = Array.from(new Set(learnerProgressData.map(r => r.module))).sort((a, b) => a.localeCompare(b));
                const options = ['All Modules', ...uniqueModules];
                const q = moduleSearch.trim().toLowerCase();
                const filtered = q ? options.filter(n => n.toLowerCase().includes(q)) : options;
                if (filtered.length === 0) {
                  return <div className="px-4 py-3 text-sm text-gray-500" data-id="element-342" data-mp="1mizlrbl6cqp9">
                          No skill modules found
                        </div>;
                }
                return filtered.map(name => <button key={name} onClick={() => {
                  setModuleFilter(name);
                  setIsModuleDropdownOpen(false);
                  setModuleSearch('');
                  setCurrentPage(1);
                }} className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm ${moduleFilter === name ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-343" data-mp="18kj3qohv3drt">
                        {name}
                      </button>);
              })()}
                </div>
              </div>}
          </div>

          <ResetFiltersButton isActive={moduleFilter !== 'All Modules'} onReset={() => {
          setModuleFilter('All Modules');
          setModuleSearch('');
          setIsModuleDropdownOpen(false);
          setCurrentPage(1);
        }} className="sm:flex-shrink-0" data-id="element-344" data-mp="32swrvw11lwtu" />

          <button type="button" className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white text-sm font-medium rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-345" data-mp="2oydqnnwu4zd4">
            <Download size={16} data-id="element-346" data-mp="24yvk3u9fm84m" />
            Download
          </button>
        </div>
      </div>

      {/* Table */}
      {showDetails && <div className="overflow-x-auto" data-id="element-347" data-mp="29vvrq9lxjixu">
          <table className="w-full text-sm" data-id="element-348" data-mp="2ewjpde4nyy11">
            <thead data-id="element-349" data-mp="2exuupidrcdu9">
              <tr className="border-b border-gray-200 bg-gray-50" data-id="element-350" data-mp="2exbfi9fhs3jx">
                <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('name')}`} data-id="element-351" data-mp="2julas5pkb9fo">
                  <button onClick={() => handleSort('name')} className={headerBtnClass('name')} data-id="element-352" data-mp="1r6pfn5fn5pl0">
                    Learner Name
                    <SortArrow columnKey="name" data-id="element-353" data-mp="20ws5iwrgunf4" />
                  </button>
                </th>
                <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('module')}`} data-id="element-354" data-mp="13hebddz31d24">
                  <button onClick={() => handleSort('module')} className={headerBtnClass('module')} data-id="element-355" data-mp="18ewnfd8ec8ec">
                    Skill Module
                    <SortArrow columnKey="module" data-id="element-356" data-mp="vg5lcm2e1n8k" />
                  </button>
                </th>
                <th data-phoebe-tour="learner-progress-scores" className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('attempts')}`} data-id="element-357" data-mp="1a51r2cgz2o60">
                  <button onClick={() => handleSort('attempts')} className={headerBtnClass('attempts')} data-id="element-358" data-mp="hiqst8gu8zcd">
                    Attempts
                    <SortArrow columnKey="attempts" data-id="element-359" data-mp="rd0l6q5s5xn3" />
                  </button>
                </th>
                <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('firstScore')}`} data-id="element-360" data-mp="mi3f9s3ss4ef">
                  <button onClick={() => handleSort('firstScore')} className={headerBtnClass('firstScore')} data-id="element-361" data-mp="1xue11ek4j09d">
                    First Score
                    <SortArrow columnKey="firstScore" data-id="element-362" data-mp="2afcl2mbxxalz" />
                  </button>
                </th>
                <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('finalScore')}`} data-id="element-363" data-mp="1wfkz9zf6mpxf">
                  <button onClick={() => handleSort('finalScore')} className={headerBtnClass('finalScore')} data-id="element-364" data-mp="2o4wjjfyhnlph">
                    Final Score
                    <SortArrow columnKey="finalScore" data-id="element-365" data-mp="2fdnu4uuevh35" />
                  </button>
                </th>
                <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('improvement')}`} data-id="element-366" data-mp="2jdobooct48ef">
                  <button onClick={() => handleSort('improvement')} className={headerBtnClass('improvement')} data-id="element-367" data-mp="3buigdwre1oub">
                    Improvement
                    <SortArrow columnKey="improvement" data-id="element-368" data-mp="1j7m1vlrjesm8" />
                  </button>
                </th>
                <th data-phoebe-tour="learner-progress-confidence" className={`text-left px-4 sm:px-6 py-3 max-w-[160px] transition-colors ${headerActiveClass('ccFirst')}`} data-id="element-369" data-mp="1e7zu3ruha4kt">
                  <button onClick={() => handleSort('ccFirst')} className={`${headerBtnClass('ccFirst')} items-start text-left`} data-id="element-370" data-mp="26vvnonor3gdd">
                    <span className="leading-snug" data-id="element-371" data-mp="1x6c4fgx4lkhz">
                      Confident & Correct Rate First Attempt
                    </span>
                    <SortArrow columnKey="ccFirst" data-id="element-372" data-mp="1t3fpc06so6is" />
                  </button>
                </th>
                <th className={`text-left px-4 sm:px-6 py-3 max-w-[160px] transition-colors ${headerActiveClass('ccFinal')}`} data-id="element-373" data-mp="2ppj0ft3cunnl">
                  <button onClick={() => handleSort('ccFinal')} className={`${headerBtnClass('ccFinal')} items-start text-left`} data-id="element-374" data-mp="7f3k0p2y1pxk">
                    <span className="leading-snug" data-id="element-375" data-mp="gegv1m6pumke">
                      Confident & Correct Rate Final Attempt
                    </span>
                    <SortArrow columnKey="ccFinal" data-id="element-376" data-mp="lf4mua2rr7fo" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody data-id="element-377" data-mp="vbr5cs7mhmej">
              {paginatedData.map((row, idx) => <tr key={idx} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors" data-id="element-378" data-mp="v9wih8csjk7t" data-mpi={idx}>
                  <td className={`px-4 sm:px-6 py-4 font-medium text-gray-900 align-middle whitespace-nowrap transition-colors ${cellActiveClass('name')}`} data-id="element-379" data-mp="143t3gju7yqln">
                    {row.name}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle transition-colors ${cellActiveClass('module')}`} data-id="element-380" data-mp="3a5dme3k9qi5p">
                    {row.module}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums transition-colors ${cellActiveClass('attempts')}`} data-id="element-381" data-mp="3jvq1hg9bdj4g">
                    {row.attempts}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('firstScore')}`} data-id="element-382" data-mp="3subllfufp67c">
                    {row.firstScore}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('finalScore')}`} data-id="element-383" data-mp="1qv7pizto4d4">
                    {row.finalScore}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('improvement')}`} data-id="element-384" data-mp="iz5ucunho6cg">
                    {row.improvement}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums max-w-[160px] transition-colors ${cellActiveClass('ccFirst')}`} data-id="element-385" data-mp="992uqblv3u55">
                    {row.ccFirst}
                  </td>
                  <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums max-w-[160px] transition-colors ${cellActiveClass('ccFinal')}`} data-id="element-386" data-mp="58am9kktdt9x">
                    {row.ccFinal}
                  </td>
                </tr>)}
            </tbody>
          </table>

          {/* Pagination */}
          {sortedData.length > 0 && <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-200" data-id="element-387" data-mp="1po4g8kzcnood">
              <p className="text-sm text-gray-600" data-id="element-388" data-mp="deirp3whcaqx">
                Showing {(safeCurrentPage - 1) * itemsPerPage + 1}–
                {Math.min(safeCurrentPage * itemsPerPage, sortedData.length)} of{' '}
                {sortedData.length}
              </p>
              <div className="flex items-center gap-2" data-id="element-389" data-mp="3m38k39g5g4g">
                <span className="text-sm text-gray-600" data-id="element-390" data-mp="3utxxab2fzu7l">Page</span>
                <div className="relative" ref={paginationDropdownRef} data-id="element-391" data-mp="3pve6nxcwlu8w">
                  <button type="button" onClick={() => setIsPaginationDropdownOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isPaginationDropdownOpen} aria-label="Select page" className={`flex min-w-14 items-center justify-between rounded-md border bg-white py-1 pl-2 pr-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D]/20 ${isPaginationDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20 text-[#30718D]' : 'border-gray-300 text-gray-700'}`} data-id="element-392" data-mp="3ptba0uh8x2v7">
                    <span data-id="element-393" data-mp="1mm2iayzp3cf7">{safeCurrentPage}</span>
                    <ChevronDown size={14} aria-hidden="true" className={`ml-2 text-gray-400 transition-transform ${isPaginationDropdownOpen ? 'rotate-180' : ''}`} data-id="element-394" data-mp="1mkrfb1e2q8p5" />
                  </button>
                  {isPaginationDropdownOpen && <div role="listbox" aria-label="Select page" className="absolute bottom-full z-30 mb-1 max-h-60 min-w-full overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-395" data-mp="13nq7l8t4xuhi">
                      {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} type="button" role="option" aria-selected={safeCurrentPage === page} onClick={() => {
                setCurrentPage(page);
                setIsPaginationDropdownOpen(false);
              }} className={`flex w-full items-center border-b border-gray-100 px-3 py-2 text-left text-sm transition-colors last:border-b-0 ${safeCurrentPage === page ? 'bg-[#30718D]/10 font-medium text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-396" data-mp="3dqflac2oobzn">
                          {page}
                        </button>)}
                    </div>}
                </div>
                <span className="text-sm text-gray-600" data-id="element-397" data-mp="k4qy6basa4s3">of {totalPages}</span>
              </div>
            </div>}
        </div>}
    </div>;
}
function ActionItemsView({
  actionItems,
  selectedSkill
}) {
  const [activeFilter, setActiveFilter] = useState<'all' | 'urgent' | 'attention' | 'celebrate'>('all');
  const [selectedAction, setSelectedAction] = useState<number | null>(null);
  const [viewType, setViewType] = useState<'all' | 'individual' | 'macro'>('all');
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  // Filter action items by selected skill
  const skillFilteredItems = selectedSkill === 'All Skills Combined' ? actionItems : actionItems.filter(item => item.module === selectedSkill || item.module === 'All Modules' || item.module === 'All Skills' || item.module === 'Multiple Modules');
  const filteredItems = skillFilteredItems.filter(item => {
    // Filter by view type
    if (viewType === 'individual' && item.type === 'macro') return false;
    if (viewType === 'macro' && item.type !== 'macro') return false;
    // Filter by priority
    if (activeFilter === 'all') return true;
    if (activeFilter === 'urgent') return item.priority === 'high';
    if (activeFilter === 'attention') return item.priority === 'medium';
    if (activeFilter === 'celebrate') return item.priority === 'positive';
    return true;
  });
  const urgentCount = skillFilteredItems.filter(a => a.priority === 'high').length;
  const attentionCount = skillFilteredItems.filter(a => a.priority === 'medium').length;
  const celebrateCount = skillFilteredItems.filter(a => a.priority === 'positive').length;
  const macroCount = skillFilteredItems.filter(a => a.type === 'macro').length;
  const individualCount = skillFilteredItems.filter(a => a.type !== 'macro').length;
  // Get recipient name for modals
  const getRecipientName = (item: any) => {
    if (item.learner) return item.learner.name;
    return 'Team';
  };
  const getRecipientFirstName = (item: any) => {
    if (item.learner) return item.learner.name.split(' ')[0];
    return 'Team';
  };
  return <div data-id="element-398" data-mp="2fxpoov6ukxbk">
      {/* Message Modal */}
      <AnimatePresence data-id="element-399" data-mp="2bu1jgj5oudth">
        {messageModal.isOpen && messageModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMessageModal({
        isOpen: false,
        item: null
      })} data-id="element-400" data-mp="273beh716cczb">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-401" data-mp="2efr4jbr2j36j">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-402" data-mp="2ocm0bmvyiph4">
                <div className="flex items-center gap-3" data-id="element-403" data-mp="2ta428tbk44fm">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-404" data-mp="2t8kjmw2fhhao">
                    <Mail size={24} className="text-white" data-id="element-405" data-mp="3gyn8l3b1q752" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-406" data-mp="3ltb07b1cjhnv">
                    Send Message
                  </h2>
                </div>
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-407" data-mp="3r4yc3ir6lugn">
                  <X size={20} className="text-gray-500" data-id="element-408" data-mp="3c6e2xlblliet" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-409" data-mp="387dki3x18hsc">
                <p className="text-gray-600 mb-1" data-id="element-410" data-mp="3m5bu6w0erq1i">
                  Your message has been successfully sent to
                </p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-411" data-mp="3h57eh87bqzcc">
                  {getRecipientName(messageModal.item)}
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-412" data-mp="11h7bgd1o3v8s">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-413" data-mp="wfruox6iwaw7">Message Preview:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-414" data-mp="14fxtth5w9y9u">
                    "Hi {getRecipientFirstName(messageModal.item)}, I noticed
                    your recent assessment activity and wanted to reach out to
                    discuss your progress. Let's schedule a time to connect."
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-415" data-mp="ktroxje4d7bd">
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-416" data-mp="pra0lhjiu0n2">
                  Cancel
                </button>
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-417" data-mp="70a5dylfnulc">
                  <Send size={18} data-id="element-418" data-mp="3gowambtuubkg" />
                  Send
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Meeting Modal */}
      <AnimatePresence data-id="element-419" data-mp="37wucwlb679dv">
        {meetingModal.isOpen && meetingModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMeetingModal({
        isOpen: false,
        item: null
      })} data-id="element-420" data-mp="32xq621bpxnqp">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-421" data-mp="go7fw8tdfjgg">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-422" data-mp="3k83j6q7qk4oy">
                <div className="flex items-center gap-3" data-id="element-423" data-mp="3p6wvifbadpnj">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-424" data-mp="3b62qm6ng66tm">
                    <Video size={24} className="text-white" data-id="element-425" data-mp="3f7fvoeipu5zh" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-426" data-mp="3f9iloyjvbz47">
                    Schedule Meeting
                  </h2>
                </div>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-427" data-mp="2rlc4j9wk9c70">
                  <X size={20} className="text-gray-500" data-id="element-428" data-mp="3fgycv30nyc3m" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-429" data-mp="3ahc1z08b57xz">
                <p className="text-gray-600 mb-1" data-id="element-430" data-mp="3oat9tnrmsw86">Schedule a meeting with</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-431" data-mp="3k8yd6wknb70q">
                  {getRecipientName(meetingModal.item)}
                </p>

                <div className="space-y-4" data-id="element-432" data-mp="244ukpgimk19">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-433" data-mp="3t9o35jn8636j">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-434" data-mp="3t7jrugmzy1w8">Meeting Topic:</p>
                    <p className="text-gray-700 font-medium" data-id="element-435" data-mp="9lfb96uqvs32">
                      {meetingModal.item.insight}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-436" data-mp="jlfvqw4coe8u">
                    <div data-id="element-437" data-mp="jitpczt744s7">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-438" data-mp="emcrvf03we2g">
                        Date
                      </label>
                      <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-439" data-mp="3n2ehdanfwapf" />
                    </div>
                    <div data-id="element-440" data-mp="34bez0sag9z54">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-441" data-mp="34alrhp3o35lx">
                        Time
                      </label>
                      <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue="10:00" data-id="element-442" data-mp="2za7ssxnyh80k" />
                    </div>
                  </div>

                  <div data-id="element-443" data-mp="19ux3yv9iwh8a">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-444" data-mp="19uf13fscv4re">
                      Duration
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-445" data-mp="1xiofvxr5is91">
                      <option data-id="element-446" data-mp="1okujkmtdzylv">15 minutes</option>
                      <option selected data-id="element-447" data-mp="1om5mylix74cg">30 minutes</option>
                      <option data-id="element-448" data-mp="1skmxlf1juwym">45 minutes</option>
                      <option data-id="element-449" data-mp="1sniv529hjjmj">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-450" data-mp="h82lu506htxz">
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-451" data-mp="2jtg2xi51o9ea">
                  Cancel
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-452" data-mp="213gf0ykxx0cg">
                  <Calendar size={18} data-id="element-453" data-mp="3lh8r0w8e1e9t" />
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Kudos Modal */}
      <AnimatePresence data-id="element-454" data-mp="3ozy0je4xrv91">
        {kudosModal.isOpen && kudosModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setKudosModal({
        isOpen: false,
        item: null
      })} data-id="element-455" data-mp="3t0zvhirtll57">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-456" data-mp="3f2jnloi81olh">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-457" data-mp="23akcrdhqppw2">
                <div className="flex items-center gap-3" data-id="element-458" data-mp="2habw7tr0zyhg">
                  <div className="w-12 h-12 rounded-lg bg-[#007558] flex items-center justify-center" data-id="element-459" data-mp="2c962ptpjk76v">
                    <ThumbsUp size={24} className="text-white" data-id="element-460" data-mp="2r6euupmo5ezn" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-461" data-mp="2m83leg0ad6hy">
                    Give Kudos
                  </h2>
                </div>
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-462" data-mp="2zxyst9jhfktk">
                  <X size={20} className="text-gray-500" data-id="element-463" data-mp="34zwhijanj7tw" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-464" data-mp="3tj7xyyu3zrx1">
                <p className="text-gray-600 mb-1" data-id="element-465" data-mp="3tjhmehzi9r0m">Send kudos to celebrate</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-466" data-mp="3em7efim2n39n">
                  {getRecipientName(kudosModal.item)}
                </p>

                <div className="bg-[#007558]/5 rounded-lg p-4 border border-[#007558]/20 mb-4" data-id="element-467" data-mp="3jjh1wspwh4uu">
                  <div className="flex items-center gap-2 mb-2" data-id="element-468" data-mp="35svjt689syy0">
                    <Star size={18} className="text-[#007558]" data-id="element-469" data-mp="3akvpzgexoo31" />
                    <p className="text-sm font-medium text-[#007558]" data-id="element-470" data-mp="3am6rtaotjlri">
                      Achievement:
                    </p>
                  </div>
                  <p className="text-gray-700" data-id="element-471" data-mp="30vlurrosmpap">{kudosModal.item.insight}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-472" data-mp="2mvuc3dnc0kqd">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-473" data-mp="2qwmk63kgwjl2">Kudos Message:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-474" data-mp="xdnppo0yr7a6">
                    "Congratulations {getRecipientFirstName(kudosModal.item)}!
                    🎉 Your hard work and dedication have really paid off. Keep
                    up the amazing work!"
                  </p>
                </div>

                <div className="mt-4" data-id="element-475" data-mp="ip15p90e5rcm">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-476" data-mp="iqcclhp4vf7y">
                    Add a personal note (optional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007558] focus:border-transparent resize-none" rows={3} placeholder="Add your personal message..." data-id="element-477" data-mp="1bcx3kh1p5paz" />
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-478" data-mp="3r2glaydd2we7">
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-479" data-mp="3n1eiy8o5e480">
                  Cancel
                </button>
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-480" data-mp="2gdmz2inav1ke">
                  <ThumbsUp size={18} data-id="element-481" data-mp="2zaemn49vk2u9" />
                  Send Kudos
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Skill Filter Indicator */}
      {selectedSkill !== 'All Skills Combined'}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6" data-id="element-482" data-mp="36sm8vuo3bol8">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-483" data-mp="32sc0ycgqmo3q">
          <div className="flex items-center justify-between mb-3" data-id="element-484" data-mp="2nxxj4f8wtkqn">
            <div className="w-12 h-12 rounded-lg bg-[#DB3725]/10 flex items-center justify-center" data-id="element-485" data-mp="2jvknwkrsh584">
              <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-486" data-mp="2xutrmkyt66v7" />
            </div>
            <span className="text-3xl font-bold text-[#DB3725]" data-id="element-487" data-mp="2stwckh93hsxx">
              {urgentCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-488" data-mp="d28jiwx7ckke">
            Urgent Actions
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-489" data-mp="84gr81snawfo">
            Require immediate attention
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-490" data-mp="1v1erantqhbix">
          <div className="flex items-center justify-between mb-3" data-id="element-491" data-mp="29q2kwok0rb68">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center" data-id="element-492" data-mp="2drwjmranm53r">
              <Clock size={24} className="text-yellow-600" data-id="element-493" data-mp="200j37m81lncy" />
            </div>
            <span className="text-3xl font-bold text-yellow-600" data-id="element-494" data-mp="1zyf199tpeyfb">
              {attentionCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-495" data-mp="2spgl3o8cdi56">
            Needs Attention
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-496" data-mp="2xgf2ys8zaoaj">Monitor and support</div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-497" data-mp="3b1qfy3g0lnl">
          <div className="flex items-center justify-between mb-3" data-id="element-498" data-mp="h0vfagey75xb">
            <div className="w-12 h-12 rounded-lg bg-[#007558]/10 flex items-center justify-center" data-id="element-499" data-mp="3ghbl5z9f5ms2">
              <Star size={24} className="text-[#007558]" data-id="element-500" data-mp="3lfv5jv8d6qib" />
            </div>
            <span className="text-3xl font-bold text-[#007558]" data-id="element-501" data-mp="3lhzjz73w37wu">
              {celebrateCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-502" data-mp="3uhclndlrus70">
            Celebrate Success
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-503" data-mp="14sajkeqabxb7">
            Recognize achievements
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-504" data-mp="19p0z90y6gqw3">
          <div className="flex items-center justify-between mb-3" data-id="element-505" data-mp="1a2mqijaxuwba">
            <div className="w-12 h-12 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-506" data-mp="162cnnfig0m0n">
              <CheckCircle size={24} className="text-[#30718D]" data-id="element-507" data-mp="114uebo0ik4s4" />
            </div>
            <span className="text-3xl font-bold text-[#30718D]" data-id="element-508" data-mp="w3wtt4s16yng">
              {skillFilteredItems.length}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-509" data-mp="r7y1j63l0whr">Total Actions</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-510" data-mp="n5uoid19ljud">
            {selectedSkill === 'All Skills Combined' ? 'All recommended items' : `For ${selectedSkill}`}
          </div>
        </motion.div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6" data-id="element-511" data-mp="ev40s3nzn22h">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4" data-id="element-512" data-mp="jm2ldfg0embj">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2" data-id="element-513" data-mp="5vh949l29amm">
            <div className="flex items-center gap-2" data-id="element-514" data-mp="9w9j551rbjjy">
              <Filter size={18} className="text-gray-600" data-id="element-515" data-mp="9wsrbu1lutks" />
              <span className="text-sm font-medium text-gray-700" data-id="element-516" data-mp="xlvibsi4pyry">
                Filter by:
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-517" data-mp="12komuvffuffz">
              <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-518" data-mp="om8fr3dnymxb">
                All ({skillFilteredItems.length})
              </button>
              <button onClick={() => setActiveFilter('urgent')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'urgent' ? 'bg-[#DB3725] text-white' : 'bg-[#DB3725]/10 text-[#DB3725] hover:bg-[#DB3725]/20'}`} data-id="element-519" data-mp="3e8ob24u336by">
                Urgent ({urgentCount})
              </button>
              <button onClick={() => setActiveFilter('attention')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'attention' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`} data-id="element-520" data-mp="3au4vluwoec56">
                Needs Attention ({attentionCount})
              </button>
              <button onClick={() => setActiveFilter('celebrate')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'celebrate' ? 'bg-[#007558] text-white' : 'bg-[#007558]/10 text-[#007558] hover:bg-[#007558]/20'}`} data-id="element-521" data-mp="2s229i419m3nj">
                Celebrate ({celebrateCount})
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-522" data-mp="17mfm8atqyxk7">
            <span className="text-sm font-medium text-gray-700" data-id="element-523" data-mp="17j038wwyd4di">View:</span>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap" data-id="element-524" data-mp="1bloetcd1wpe6">
              <button onClick={() => setViewType('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-525" data-mp="1gf92obkxk2zn">
                All
              </button>
              <button onClick={() => setViewType('individual')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'individual' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-526" data-mp="1v8ufpddd0qx8">
                Individual ({individualCount})
              </button>
              <button onClick={() => setViewType('macro')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'macro' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-527" data-mp="3tlfso1ppl1bu">
                System-Wide ({macroCount})
              </button>
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-600" data-id="element-528" data-mp="ge4b6ytctp9t">
          Showing {filteredItems.length} of {skillFilteredItems.length} items
          {selectedSkill !== 'All Skills Combined' && ` (filtered by ${selectedSkill})`}
        </div>
      </div>

      {/* Action Items List */}
      <div className="space-y-4" data-id="element-529" data-mp="2ecz53meeiyg">
        <AnimatePresence mode="popLayout" data-id="element-530" data-mp="2dbce1w0s80w">
          {filteredItems.map((item, index) => <motion.div key={item.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          delay: index * 0.05
        }} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200" data-id="element-531" data-mp="2s4gg6i20ukh9" data-mpi={index}>
              {/* Header Row - Stack on mobile */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-1 gap-2" data-id="element-532" data-mp="25uhpd4pgoafw">
                <div className="flex items-center gap-4" data-id="element-533" data-mp="2am85jrb2xsf8">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${item.priority === 'high' ? 'bg-[#DB3725]/10 border-2 border-[#DB3725]' : item.priority === 'medium' ? 'bg-[#F59E0B]/10 border-2 border-[#F59E0B]' : 'bg-[#007558]/10 border-2 border-[#007558]'}`} data-id="element-534" data-mp="1vx3m66sjti5j">
                    {item.priority === 'high' ? <AlertTriangle size={20} className="text-[#DB3725] sm:w-6 sm:h-6" data-id="element-535" data-mp="188wz61ik810f" /> : item.priority === 'medium' ? <Clock size={20} className="text-[#F59E0B] sm:w-6 sm:h-6" data-id="element-536" data-mp="1hb67ckqcvian" /> : <Star size={20} className="text-[#007558] sm:w-6 sm:h-6" data-id="element-537" data-mp="1r7rdj9e42zg2" />}
                  </div>

                  {/* Name and Badge */}
                  <div data-id="element-538" data-mp="20wsvv684psx7">
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-id="element-539" data-mp="2106ty29rffyu">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-540" data-mp="1vzj3pitcv35o">
                        {item.learner ? item.learner.name : 'System Alert'}
                      </h4>
                      <span className={`px-2 sm:px-3 py-1 rounded text-xs font-semibold ${item.priority === 'high' ? 'bg-[#DB3725] text-white' : item.priority === 'medium' ? 'bg-white text-gray-900 border border-gray-900' : 'bg-white text-gray-900 border border-gray-900'}`} data-id="element-541" data-mp="251abct7ghk26">
                        {item.priority === 'high' ? 'Urgent' : item.priority === 'medium' ? 'Attention' : 'Celebrate'}
                      </span>
                    </div>
                    {item.learner && <p className="text-sm text-gray-500 mt-0.5 hidden sm:block" data-id="element-542" data-mp="32mbtogf4y1no">
                        {item.learner.email}
                      </p>}
                    {item.type === 'macro' && <p className="text-sm text-gray-500 mt-0.5" data-id="element-543" data-mp="29yfzpjgyanul">
                        {item.affectedLearners} learners affected
                      </p>}
                  </div>
                </div>

                {/* Module and Time - Below name on mobile */}
                <div className="text-left sm:text-right ml-14 sm:ml-0" data-id="element-544" data-mp="22dn8hv75461n">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base" data-id="element-545" data-mp="22cc5vypfl4c4">
                    {item.module}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500" data-id="element-546" data-mp="1te8gusf8lpfk">
                    {item.lastActivity}
                  </p>
                </div>
              </div>

              {/* Insight */}
              <div className="flex items-start gap-2 mt-4 mb-2" data-id="element-547" data-mp="1jndtfkq98jsj">
                <Zap size={18} className="text-gray-700 mt-0.5 flex-shrink-0" data-id="element-548" data-mp="1eokh3vmpeyty" />
                <p className="font-semibold text-gray-900" data-id="element-549" data-mp="1emhullkrfntz">{item.insight}</p>
              </div>

              {/* Details */}
              <p className="text-sm text-gray-600 ml-6 mb-5 leading-relaxed" data-id="element-550" data-mp="15nmnm79w69pm">
                {item.details}
              </p>

              {/* Suggested Actions */}
              <div className="mb-5" data-id="element-551" data-mp="10qw8bm5wvi59">
                <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-552" data-mp="1fbbdjewtl2n7">
                  Suggested Actions
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-553" data-mp="1aaxg0qsuhb3f">
                  {item.suggestedActions.map((action, idx) => <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-800 rounded text-sm font-medium border border-gray-200" data-id="element-554" data-mp="16adktsru0hjx" data-mpi={idx}>
                      {action}
                    </span>)}
                </div>
              </div>

              {/* Action Buttons - Wrap on mobile */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-5 border-t border-gray-200" data-id="element-555" data-mp="1p7y0c98pi3rv">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-id="element-556" data-mp="1k9d5kpc84cat">
                  <button onClick={() => setMessageModal({
                isOpen: true,
                item
              })} className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-all text-sm font-medium" data-id="element-557" data-mp="2hqbvg12nrbn9">
                    <Mail size={16} data-id="element-558" data-mp="2u7340szdqb74" />
                    <span className="hidden sm:inline" data-id="element-559" data-mp="2ua8uabjqvj4f">Send Message</span>
                  </button>
                  <button onClick={() => setMeetingModal({
                isOpen: true,
                item
              })} className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium" data-id="element-560" data-mp="2y7526s5womhn">
                    <Video size={16} data-id="element-561" data-mp="3lztusnyan7oz" />
                    <span className="hidden sm:inline" data-id="element-562" data-mp="3m0vj3u4blqon">Schedule Meeting</span>
                  </button>
                  {item.priority === 'positive' && <button onClick={() => setKudosModal({
                isOpen: true,
                item
              })} className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-all text-sm font-medium" data-id="element-563" data-mp="1qv49ku5on0c8">
                      <ThumbsUp size={16} data-id="element-564" data-mp="2alpso6uyor0x" />
                      <span className="hidden sm:inline" data-id="element-565" data-mp="25ll927yrbg6h">Send Kudos</span>
                    </button>}
                </div>
                <button className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors self-start sm:self-auto" data-id="element-566" data-mp="2jjrxkxhtg3th">
                  Dismiss
                </button>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
// One insight per Data Explorer chart, in the same order as chartTypeOptions
const CHART_INSIGHT_PAGES = [{
  label: 'Score Distribution',
  headline: 'Most learners cluster just above passing',
  insightBeforeMetric: 'The largest group of learners scores',
  highlightedMetric: '70–79%',
  insightAfterMetric: ', just above the pass mark.',
  explanation: 'These learners are passing without much margin, so a short refresher before their next attempt is the quickest way to lift the whole curve.',
  insightId: 10
}, {
  label: 'Performance by Attempt',
  headline: 'First-attempt scores are the clearest improvement lever',
  insightBeforeMetric: 'Learners gain roughly',
  highlightedMetric: '13 points',
  insightAfterMetric: ' between their first and final attempts.',
  explanation: 'Retakes are doing the work that preparation should. Encouraging practice before the first attempt would reduce how many learners need a second try.',
  insightId: 10
}, {
  label: 'Answer Confidence Breakdown',
  headline: 'Confident but incorrect answers are the risk to watch',
  insightBeforeMetric: 'Learners answered confidently yet incorrectly on',
  highlightedMetric: '18% of questions',
  insightAfterMetric: ', the clearest sign of confident errors.',
  explanation: 'These learners will not ask for help because they believe they are right, so they benefit most from a direct nudge toward reviewing material.',
  insightId: 10
}];
function AggregateView({
  totalLearners,
  totalAssessments,
  overallAvgScore,
  overallMastery,
  moduleData,
  scoreDistribution,
  attemptTrends,
  COLORS,
  selectedSkill,
  setSelectedSkill,
  isSkillDropdownOpen,
  setIsSkillDropdownOpen,
  skillDropdownRef,
  skillOptions,
  selectedLearner,
  setSelectedLearner,
  selectedLearnerData,
  isLearnerDropdownOpen,
  setIsLearnerDropdownOpen,
  learnerDropdownRef
}) {
  const [selectedInsightSkill, setSelectedInsightSkill] = useState<string>('All Skills Combined');
  const [isInsightSkillDropdownOpen, setIsInsightSkillDropdownOpen] = useState(false);
  const insightSkillDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedLeaderboardSkill, setSelectedLeaderboardSkill] = useState<string>('All Skills Combined');
  const [isLeaderboardSkillDropdownOpen, setIsLeaderboardSkillDropdownOpen] = useState(false);
  const leaderboardSkillDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedLeaderboardMetric, setSelectedLeaderboardMetric] = useState<string>('Average Score');
  const [isLeaderboardMetricDropdownOpen, setIsLeaderboardMetricDropdownOpen] = useState(false);
  const leaderboardMetricDropdownRef = useRef<HTMLDivElement>(null);
  const [performanceLearnerFilter, setPerformanceLearnerFilter] = useState<string>('All Learners');
  const [performanceLearnerSearch, setPerformanceLearnerSearch] = useState<string>('');
  const [isPerformanceLearnerDropdownOpen, setIsPerformanceLearnerDropdownOpen] = useState(false);
  const performanceLearnerDropdownRef = useRef<HTMLDivElement>(null);
  const [performanceSortKey, setPerformanceSortKey] = useState<PerformanceTableSortKey>('confidentIncorrect');
  const [performanceSortDir, setPerformanceSortDir] = useState<'asc' | 'desc'>('desc');
  const [performancePage, setPerformancePage] = useState(1);
  const [isPerformancePageDropdownOpen, setIsPerformancePageDropdownOpen] = useState(false);
  const performancePageDropdownRef = useRef<HTMLDivElement>(null);
  const performancePerPage = 10;
  const handlePerformanceSort = (key: PerformanceTableSortKey) => {
    if (performanceSortKey === key) {
      setPerformanceSortDir(performanceSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setPerformanceSortKey(key);
      setPerformanceSortDir('asc');
    }
    setPerformancePage(1);
  };
  const [selectedChartType, setSelectedChartType] = useState<string>('Score Distribution');
  const [isChartTypeDropdownOpen, setIsChartTypeDropdownOpen] = useState(false);
  const chartTypeDropdownRef = useRef<HTMLDivElement>(null);
  const chartTypeOptions = ['Score Distribution', 'Performance by Attempt', 'Answer Confidence Breakdown'];
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const handleDownloadChart = () => {
    if (!chartAreaRef.current) return;
    const svgElement = chartAreaRef.current.querySelector('svg');
    if (!svgElement) return;
    const svgClone = svgElement.cloneNode(true) as SVGElement;
    // Ensure the SVG has explicit dimensions
    const bbox = svgElement.getBoundingClientRect();
    svgClone.setAttribute('width', String(bbox.width));
    svgClone.setAttribute('height', String(bbox.height));
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const svgBlob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8'
    });
    const url = URL.createObjectURL(svgBlob);
    const canvas = document.createElement('canvas');
    const scale = 2; // retina quality
    canvas.width = bbox.width * scale;
    canvas.height = bbox.height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${selectedChartType.replace(/\s+/g, '_')}_chart.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    img.src = url;
  };
  // Local learner state for Data Explorer (doesn't switch to IndividualView)
  const [selectedChartLearner, setSelectedChartLearner] = useState<number | null>(null);
  const [isChartLearnerDropdownOpen, setIsChartLearnerDropdownOpen] = useState(false);
  const chartLearnerDropdownRef = useRef<HTMLDivElement>(null);
  const selectedChartLearnerData = selectedChartLearner ? mockLearners.find(l => l.id === selectedChartLearner) : null;
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (insightSkillDropdownRef.current && !insightSkillDropdownRef.current.contains(event.target as Node)) {
        setIsInsightSkillDropdownOpen(false);
      }
      if (leaderboardSkillDropdownRef.current && !leaderboardSkillDropdownRef.current.contains(event.target as Node)) {
        setIsLeaderboardSkillDropdownOpen(false);
      }
      if (leaderboardMetricDropdownRef.current && !leaderboardMetricDropdownRef.current.contains(event.target as Node)) {
        setIsLeaderboardMetricDropdownOpen(false);
      }
      if (chartTypeDropdownRef.current && !chartTypeDropdownRef.current.contains(event.target as Node)) {
        setIsChartTypeDropdownOpen(false);
      }
      if (chartLearnerDropdownRef.current && !chartLearnerDropdownRef.current.contains(event.target as Node)) {
        setIsChartLearnerDropdownOpen(false);
      }
      if (performanceLearnerDropdownRef.current && !performanceLearnerDropdownRef.current.contains(event.target as Node)) {
        setIsPerformanceLearnerDropdownOpen(false);
      }
      if (performancePageDropdownRef.current && !performancePageDropdownRef.current.contains(event.target as Node)) {
        setIsPerformancePageDropdownOpen(false);
      }
    };
    if (isInsightSkillDropdownOpen || isLeaderboardSkillDropdownOpen || isLeaderboardMetricDropdownOpen || isChartTypeDropdownOpen || isChartLearnerDropdownOpen || isPerformanceLearnerDropdownOpen || isPerformancePageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInsightSkillDropdownOpen, isLeaderboardSkillDropdownOpen, isLeaderboardMetricDropdownOpen, isChartTypeDropdownOpen, isChartLearnerDropdownOpen, isPerformanceLearnerDropdownOpen, isPerformancePageDropdownOpen]);
  // Filter module data based on selected skill
  const filteredModuleData = selectedSkill === 'All Skills Combined' ? moduleData : moduleData.filter(m => m.module === selectedSkill);
  // Filter module data for insight cards based on local insight skill selector
  const insightFilteredModuleData = selectedInsightSkill === 'All Skills Combined' ? moduleData : moduleData.filter(m => m.module === selectedInsightSkill);
  // Calculate insight-filtered metrics
  const insightFilteredAvgScore = insightFilteredModuleData.length > 0 ? Math.round(insightFilteredModuleData.reduce((sum, m) => sum + m.avgScore, 0) / insightFilteredModuleData.length) : 0;
  const insightFilteredAttempts = insightFilteredModuleData.reduce((sum, m) => sum + m.attempts, 0);
  // Calculate filtered metrics
  const filteredAvgScore = filteredModuleData.length > 0 ? Math.round(filteredModuleData.reduce((sum, m) => sum + m.avgScore, 0) / filteredModuleData.length) : 0;
  const filteredMastery = filteredModuleData.length > 0 ? Math.round(filteredModuleData.reduce((sum, m) => sum + m.mastery, 0) / filteredModuleData.length) : 0;
  const filteredAttempts = filteredModuleData.reduce((sum, m) => sum + m.attempts, 0);
  // Filter score distribution based on skill (simulated - in real app this would come from backend)
  const filteredScoreDistribution = selectedSkill === 'All Skills Combined' ? scoreDistribution : scoreDistribution.map(item => ({
    ...item,
    count: Math.round(item.count * (filteredModuleData.length / moduleData.length))
  }));
  // Skill Performance header controls
  const [skillPerfTimeRange, setSkillPerfTimeRange] = useState<30 | 60 | 90>(30);
  const [skillPerfLastUpdated, setSkillPerfLastUpdated] = useState<Date>(new Date());
  const [isSkillPerfRefreshing, setIsSkillPerfRefreshing] = useState(false);
  const [isTimeRangeDropdownOpen, setIsTimeRangeDropdownOpen] = useState(false);
  const timeRangeDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeRangeDropdownRef.current && !timeRangeDropdownRef.current.contains(event.target as Node)) {
        setIsTimeRangeDropdownOpen(false);
      }
    };
    if (isTimeRangeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimeRangeDropdownOpen]);
  const handleSkillPerfRefresh = () => {
    setIsSkillPerfRefreshing(true);
    setTimeout(() => {
      setSkillPerfLastUpdated(new Date());
      setIsSkillPerfRefreshing(false);
    }, 800);
  };
  const skillPerformanceMetrics = getSkillPerformanceMetrics(selectedInsightSkill, skillPerfTimeRange);
  const formatRelativeTime = (date: Date) => {
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  };
  return <>
      {/* Aggregate Data Across All Assessments */}

      {/* Assessment Insights */}
      <div data-phoebe-tour="analytics-summary" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible mb-6" data-id="element-567" data-mp="1qcyybvw5s73d">
        {/* Assessment Insights Header */}
        <div className="bg-white px-4 sm:px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-id="element-568" data-mp="1ze5hydu3ty0u">
          <div data-id="element-569" data-mp="1ueat0ftw3iif">
            <div className="flex items-center gap-2" data-id="element-570" data-mp="1udr7kp516bzn">
              <h4 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2" data-id="element-571" data-mp="29btgzfyqtrot">
                <BarChart3 size={22} className="text-[#30718D]" data-id="element-572" data-mp="24drwcqlo5kb8" />
                Skill Performance
              </h4>
              <AIInsightPopover align="left" headline="CSS Fundamentals is a priority support opportunity" insightBeforeMetric="CSS Fundamentals has a" highlightedMetric="22% confident error rate" insightAfterMetric=", above the other core modules." explanation="Clearer practice around Flexbox, Grid, and positioning can improve readiness before the next assessment." insightId={8} data-id="element-573" data-mp="2i3ll78x6c4ky" />
            </div>
            <p className="text-sm text-gray-500 ml-[26px]" data-id="element-574" data-mp="1os6o19klau9c">
              {selectedInsightSkill === 'All Skills Combined' ? 'Detailed metrics for all skills' : `Detailed metrics for ${selectedInsightSkill}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-575" data-mp="23frtvb8un7ky">
            {/* Skill dropdown */}
            <div className="relative" ref={insightSkillDropdownRef} data-id="element-576" data-mp="28euzawsqrftg">
              <button onClick={() => setIsInsightSkillDropdownOpen(!isInsightSkillDropdownOpen)} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[200px]" data-id="element-577" data-mp="2chrc26vlohph">
                <Target size={16} className="text-[#30718D]" data-id="element-578" data-mp="n4nuzxh5emqd" />
                <span className="flex-1 text-left" data-id="element-579" data-mp="fju0f587whg4">{selectedInsightSkill}</span>
                <ChevronDown size={16} className={`transition-transform text-gray-500 ${isInsightSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-580" data-mp="as274mxofxma" />
              </button>

              {isInsightSkillDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-581" data-mp="sbcml949im7">
                  {skillOptions.map(skill => <button key={skill} onClick={() => {
                setSelectedInsightSkill(skill);
                setIsInsightSkillDropdownOpen(false);
              }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedInsightSkill === skill ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-582" data-mp="tjmpyx80crmi">
                      {skill}
                    </button>)}
                </div>}
            </div>
            {/* Time range dropdown */}
            <div className="relative" ref={timeRangeDropdownRef} data-id="element-583" data-mp="z8mk8bbt0cer">
              <button onClick={() => setIsTimeRangeDropdownOpen(!isTimeRangeDropdownOpen)} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-semibold text-gray-900 min-w-[130px]" data-id="element-584" data-mp="145czwxjp55zn">
                <Calendar size={14} className="text-[#30718D]" data-id="element-585" data-mp="1i3thxkn0eix3" />
                <span className="flex-1 text-left" data-id="element-586" data-mp="1n3z3cj2or96q">
                  Last {skillPerfTimeRange} days
                </span>
                <ChevronDown size={14} className={`transition-transform text-gray-500 ${isTimeRangeDropdownOpen ? 'rotate-180' : ''}`} data-id="element-587" data-mp="2ata5o4zopbmz" />
              </button>
              {isTimeRangeDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-588" data-mp="2kzbg2nzhabon">
                  {[30, 60, 90].map(range => <button key={range} onClick={() => {
                setSkillPerfTimeRange(range as 30 | 60 | 90);
                setIsTimeRangeDropdownOpen(false);
              }} className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${skillPerfTimeRange === range ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-589" data-mp="2tr3mmnldt0ny">
                      Last {range} days
                    </button>)}
                </div>}
            </div>
            <ResetFiltersButton isActive={selectedInsightSkill !== 'All Skills Combined' || skillPerfTimeRange !== 30} onReset={() => {
            setSelectedInsightSkill('All Skills Combined');
            setSkillPerfTimeRange(30);
            setIsInsightSkillDropdownOpen(false);
            setIsTimeRangeDropdownOpen(false);
          }} data-id="element-590" data-mp="3mf94n2l1wbk3" />
            {/* Last updated + Refresh button */}
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="p-3 sm:p-6" data-id="element-591" data-mp="h5i71kk6empu">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-592" data-mp="h6jn63g1tzef">
            {skillPerformanceMetrics.map((metric, index) => <motion.div key={metric.label} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1 + index * 0.05
          }} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6" data-id="element-593" data-mp="76stg3zbbofe" data-mpi={index}>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 sm:mb-2" data-id="element-594" data-mp="3m2cn9rp3b60x">
                  {metric.label}
                </div>
                <div className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2" data-id="element-595" data-mp="3h26qjx3yqtbz">
                  {metric.value}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 hidden sm:block mb-1 sm:mb-2" data-id="element-596" data-mp="3r15zlgsn4qzi">
                  {metric.description}
                </div>
                <div className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium ${metric.direction === 'up' ? 'text-[#007558]' : 'text-[#DB3725]'}`} data-id="element-597" data-mp="4mmnxrv1vpwq">
                  <span className="text-[10px] sm:text-xs" data-id="element-598" data-mp="ik1e4yt4o1pr">
                    {metric.direction === 'up' ? '▲' : '▼'}
                  </span>
                  <span data-id="element-599" data-mp="2ketn7vn8dy8g">
                    {metric.delta}%
                    <span className="hidden sm:inline" data-id="element-600" data-mp="2fenvygkvidqw">
                      {' '}
                      vs previous {skillPerfTimeRange} days
                    </span>
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>


      {/* Unified Data Explorer Section */}
      <div data-phoebe-tour="analytics-explorer" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible mb-6" data-id="element-601" data-mp="sbfx3dn9xgh7">
        {/* Section Header */}
        <div className="bg-white px-4 sm:px-6 py-5 border-b border-gray-200 overflow-visible" data-id="element-602" data-mp="12ax17qmvnmb2">
          <div className="flex items-center gap-2" data-id="element-603" data-mp="1798c84ows2uv">
            <h4 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2" data-id="element-604" data-mp="177ngibquydvf">
              <TrendingUp size={22} className="text-[#30718D]" data-id="element-605" data-mp="1c0qc0pf73xe9" />
              Data Explorer
            </h4>
            <AIInsightPopover align="left" headline="First-attempt scores are the clearest improvement lever" insightBeforeMetric="Learners gain roughly" highlightedMetric="13 points" insightAfterMetric=" between their first and final attempts." explanation="Comparing skills side by side shows where earlier practice would reduce retakes the most." insightId={10} pages={CHART_INSIGHT_PAGES} activePageIndex={Math.max(0, chartTypeOptions.indexOf(selectedChartType))} onPageChange={index => setSelectedChartType(chartTypeOptions[index])} data-id="element-606" data-mp="1g10f9ubls9pf" />
          </div>
          <p className="text-sm text-gray-500 ml-[26px]" data-id="element-607" data-mp="2dvuqan2ys5s1">
            Visualize and compare performance data across learners and skills
          </p>
        </div>
        {/* Header with Dropdowns */}
        <div className="p-3 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4" data-id="element-608" data-mp="252hmur79xlrp">
          {/* Chart Type Dropdown */}
          <div className="relative flex-1" ref={chartTypeDropdownRef} data-id="element-609" data-mp="2wt4ai6g45nac">
            <button onClick={() => {
            setIsChartTypeDropdownOpen(!isChartTypeDropdownOpen);
            setIsSkillDropdownOpen(false);
            setIsLearnerDropdownOpen(false);
          }} className="flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white w-full text-sm font-medium" data-id="element-610" data-mp="2hcfeg18uvoww">
              <div className="flex items-center gap-2" data-id="element-611" data-mp="302dq0rbv405a">
                <BarChart3 size={18} className="text-[#30718D]" data-id="element-612" data-mp="304zw0mj3u7le" />
                <span className="text-left" data-id="element-613" data-mp="3518hsirt4z1j">{selectedChartType}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${isChartTypeDropdownOpen ? 'rotate-180' : ''}`} data-id="element-614" data-mp="2r9k6uitaogm5" />
            </button>

            {isChartTypeDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-615" data-mp="3izeved9bw29r">
                {chartTypeOptions.map(type => <button key={type} onClick={() => {
              setSelectedChartType(type);
              setIsChartTypeDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedChartType === type ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-616" data-mp="1cbo6m665neu1">
                    {type}
                  </button>)}
              </div>}
          </div>

          {/* Skill Selector Dropdown */}
          <div className="relative flex-1" ref={skillDropdownRef} data-id="element-617" data-mp="ydr997jvn4eb">
            <button onClick={() => {
            setIsSkillDropdownOpen(!isSkillDropdownOpen);
            setIsChartLearnerDropdownOpen(false);
            setIsChartTypeDropdownOpen(false);
          }} className="flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white w-full text-sm font-medium" data-id="element-618" data-mp="yfkj0vpse4qj">
              <div className="flex items-center gap-2" data-id="element-619" data-mp="2qu0ual2e34eq">
                <Target size={18} className="text-[#30718D]" data-id="element-620" data-mp="2quk39ca2aigm" />
                <span className="text-left" data-id="element-621" data-mp="2vtdj3bctmljy">{selectedSkill}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${isSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-622" data-mp="1y5ll4f5xvgbb" />
            </button>

            {isSkillDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-623" data-mp="282hvyrjb8ej1">
                {skillOptions.map(skill => <button key={skill} onClick={() => {
              setSelectedSkill(skill);
              setIsSkillDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedSkill === skill ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-624" data-mp="1ff3x2nw1x8wa">
                    {skill}
                  </button>)}
              </div>}
          </div>

          {/* Learner Dropdown */}
          <div className="relative flex-1" ref={chartLearnerDropdownRef} data-id="element-625" data-mp="3eq5n1jxxze0k">
            <button onClick={() => {
            setIsChartLearnerDropdownOpen(!isChartLearnerDropdownOpen);
            setIsSkillDropdownOpen(false);
            setIsChartTypeDropdownOpen(false);
          }} className="flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white w-full text-sm font-medium" data-id="element-626" data-mp="39y41t8isqcye">
              <div className="flex items-center gap-2" data-id="element-627" data-mp="2r12yrt8symx3">
                <Users size={18} className="text-[#30718D]" data-id="element-628" data-mp="joul9deu3uk5" />
                <span className="text-left" data-id="element-629" data-mp="jpcqv0ndzl4q">
                  {selectedChartLearnerData ? selectedChartLearnerData.name : 'All Learners'}
                </span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${isChartLearnerDropdownOpen ? 'rotate-180' : ''}`} data-id="element-630" data-mp="ephwuo8beffg" />
            </button>

            {isChartLearnerDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-631" data-mp="4sc6xjbfsugt">
                <button onClick={() => {
              setSelectedChartLearner(null);
              setIsChartLearnerDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${selectedChartLearner === null ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-632" data-mp="3n3gt7qz782th">
                  All Learners
                </button>
                {[...mockLearners].sort((a, b) => {
              const lastNameA = a.name.split(' ').slice(-1)[0].toLowerCase();
              const lastNameB = b.name.split(' ').slice(-1)[0].toLowerCase();
              return lastNameA.localeCompare(lastNameB);
            }).map(learner => <button key={learner.id} onClick={() => {
              setSelectedChartLearner(learner.id);
              setIsChartLearnerDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${selectedChartLearner === learner.id ? 'bg-[#30718D]/5' : ''}`} data-id="element-633" data-mp="3rb6thgz31mbz">
                      <div className={`font-medium text-sm ${selectedChartLearner === learner.id ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-634" data-mp="wiwmnxuw7xto">
                        {learner.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-635" data-mp="iic492sw440p">
                        {learner.email}
                      </div>
                    </button>)}
              </div>}
          </div>

          <ResetFiltersButton isActive={selectedChartType !== 'Score Distribution' || selectedSkill !== 'All Skills Combined' || selectedChartLearner !== null} onReset={() => {
          setSelectedChartType('Score Distribution');
          setSelectedSkill('All Skills Combined');
          setSelectedChartLearner(null);
          setIsChartTypeDropdownOpen(false);
          setIsSkillDropdownOpen(false);
          setIsChartLearnerDropdownOpen(false);
        }} className="sm:flex-shrink-0" data-id="element-636" data-mp="8s0w1426b8o0" />

          {/* Download Chart Button */}
          <button onClick={handleDownloadChart} className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all relative flex items-center gap-2 bg-[#DB3725] text-white hover:bg-[#c02f1f] flex-shrink-0" data-id="element-637" data-mp="2kg5sgs31d418">
            <Download size={16} data-id="element-638" data-mp="1sq19mxuxcp24" />
            <span data-id="element-639" data-mp="1smvotutbw9c7">Download</span>
          </button>
        </div>

        {/* Chart Area */}
        <div className="p-3 sm:p-6" ref={chartAreaRef} data-id="element-640" data-mp="1wpa6ibl9h31w">
          {selectedChartType === 'Score Distribution' && <div data-id="element-641" data-mp="21j4p1bp4rbze">
              <h3 className="text-sm sm:text-lg font-semibold mb-4 sm:mb-6" data-id="element-642" data-mp="14zng12woyi5f">
                Score Distribution
                {selectedChartLearnerData ? ` - ${selectedChartLearnerData.name}` : ' - All Learners'}
                {selectedSkill !== 'All Skills Combined' ? ` - ${selectedSkill}` : ' - All Skills Combined'}
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-643" data-mp="2m1zdfhrpl1cg">
                {(() => {
              // Simulated "other students" distribution for comparison
              const otherStudentsCounts = [3, 7, 12, 18, 10];
              const dataWithOthers = filteredScoreDistribution.map((d, i) => ({
                ...d,
                others: otherStudentsCounts[i] ?? Math.round(d.count * 0.8)
              }));
              return <ComposedChart data={dataWithOthers} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-644" data-mp="1yau29fwpqx8h">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-645" data-mp="3h9i5i424t7of" />
                      <XAxis dataKey="range" axisLine={{
                  stroke: '#d1d5db'
                }} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} dy={10} data-id="element-646" data-mp="37e6p7hb2rsek" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} data-id="element-647" data-mp="3q9f0enf472rx" />
                      <Tooltip cursor={{
                  fill: 'rgba(0, 0, 0, 0.04)'
                }} contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                  padding: '8px 12px'
                }} data-id="element-648" data-mp="rn2br71d0wbw" />
                      <Bar dataKey="count" name="Learners" fill="rgba(48, 113, 141, 0.5)" stroke="#30718D" strokeWidth={1} radius={[0, 0, 0, 0]} barSize={40} data-id="element-649" data-mp="3dv9sok7ht0nn" />
                      <Line dataKey="others" name="Other Students" type="monotone" stroke="#7CB9E8" strokeWidth={2} dot={{
                  r: 4,
                  fill: '#fff',
                  stroke: '#7CB9E8',
                  strokeWidth: 2
                }} activeDot={{
                  r: 6,
                  fill: '#fff',
                  stroke: '#7CB9E8',
                  strokeWidth: 2
                }} data-id="element-650" data-mp="nf7b4ibp0ql" />
                    </ComposedChart>;
            })()}
              </ResponsiveContainer>
            </div>}

          {selectedChartType === 'Performance by Attempt' && <div data-id="element-651" data-mp="1alet0nz52sow">
              <h3 className="text-sm sm:text-lg font-semibold mb-4 sm:mb-6" data-id="element-652" data-mp="1alyc480izn4i">
                Performance by Attempt
                {selectedChartLearnerData ? ` - ${selectedChartLearnerData.name}` : ' - All Learners'}
                {selectedSkill !== 'All Skills Combined' ? ` - ${selectedSkill}` : ' - All Skills Combined'}
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-653" data-mp="zn8l0jug4039">
                <AreaChart data={attemptTrends} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-654" data-mp="uvqnk5rmshn1">
                  <defs data-id="element-655" data-mp="13n94wk0bd3hp">
                    <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1" data-id="element-656" data-mp="13q4xdste6zyr">
                      <stop offset="5%" stopColor="rgba(48, 113, 141, 0.2)" stopOpacity={1} data-id="element-657" data-mp="1ik9scbbh2cad" />
                      <stop offset="95%" stopColor="rgba(48, 113, 141, 0.0)" stopOpacity={1} data-id="element-658" data-mp="3kcxyotjexfud" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-659" data-mp="1zu3x7j4cuze4" />
                  <XAxis dataKey="attempt" axisLine={{
                stroke: '#d1d5db'
              }} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-660" data-mp="1l3d5eb2rk0b0" />
                  <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} data-id="element-661" data-mp="127wt0abyr92a" />
                  <Tooltip contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                padding: '8px 12px'
              }} data-id="element-662" data-mp="31io7xijbirns" />
                  <Area type="monotone" dataKey="avgScore" stroke="#30718D" strokeWidth={2} fillOpacity={1} fill="url(#colorScore)" dot={{
                r: 4,
                fill: '#fff',
                stroke: '#30718D',
                strokeWidth: 2
              }} activeDot={{
                r: 6,
                fill: '#fff',
                stroke: '#30718D',
                strokeWidth: 2
              }} data-id="element-663" data-mp="1ajdhg40j75p0" />
                </AreaChart>
              </ResponsiveContainer>
            </div>}

          {selectedChartType === 'Answer Confidence Breakdown' && <div data-id="element-664" data-mp="339yxg3clwg5s">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-665" data-mp="339fj0wm609wi">
                <h3 className="text-sm sm:text-lg font-bold text-gray-900" data-id="element-666" data-mp="38923nr4bosfc">
                  Answer Confidence Breakdown
                  {selectedChartLearnerData ? ` - ${selectedChartLearnerData.name}` : ' - All Learners'}
                  {selectedSkill !== 'All Skills Combined' ? ` - ${selectedSkill}` : ' - All Skills Combined'}
                </h3>
              </div>
              <div className="space-y-3 sm:space-y-4" data-id="element-667" data-mp="2ommaqbb2c4dt">
                {filteredModuleData.length > 0 ? filteredModuleData.map((module, index) => <div key={index} className="bg-[#F9FAFB] rounded-lg p-3 sm:p-5 border border-[#E5E7EB]" data-id="element-668" data-mp="3tsluka7qsmj" data-mpi={index}>
                      <div className="flex items-center justify-between mb-4 sm:mb-6" data-id="element-669" data-mp="8tw82u1xmab8">
                        <div data-id="element-670" data-mp="dkuodtmx78eh">
                          <h4 className="font-semibold text-sm sm:text-[14px] text-gray-900" data-id="element-671" data-mp="dkkye61vl18s">
                            {module.module}
                          </h4>
                        </div>
                        <div className="text-right" data-id="element-672" data-mp="3hzukvq6xjp7x">
                          <div className="text-xl sm:text-[25px] font-bold text-[#30718D] leading-none" data-id="element-673" data-mp="3i1fd395ruw2m">
                            {module.avgScore}%
                          </div>
                          <div className="text-[10px] sm:text-xs font-medium text-gray-500 mt-1" data-id="element-674" data-mp="3qyfxz5tmuc1b">
                            Highest Score
                          </div>
                        </div>
                      </div>

                      {/* Answer Confidence Breakdown */}
                      <div data-id="element-675" data-mp="1b71ux92sbath">
                        <h5 className="text-sm font-semibold text-gray-700 mb-4" data-id="element-676" data-mp="1bafygkn6pq2i">
                          Answer Confidence Breakdown
                        </h5>

                        {/* Stacked Bar Visualization */}
                        <div className="mb-7" data-id="element-677" data-mp="2m0km4tj7ygk9">
                          {/* SVG Pattern Definitions */}
                          <svg width="0" height="0" className="absolute" data-id="element-678" data-mp="2h2atoqovpu2c">
                            <defs data-id="element-679" data-mp="2d060tu6991gr">
                              <pattern id={`pattern-diagonal-${module.module}`} patternUnits="userSpaceOnUse" width="8" height="8" data-id="element-680" data-mp="2d0fqfgne16lx">
                                <rect width="8" height="8" fill="#007558" data-id="element-681" data-mp="1y3ort9ljfyxd" />
                                <path d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" data-id="element-682" data-mp="1an09592qjoa" />
                              </pattern>
                              <pattern id={`pattern-dots-${module.module}`} patternUnits="userSpaceOnUse" width="8" height="8" data-id="element-683" data-mp="1vi78s5y13eae">
                                <rect width="8" height="8" fill="#10B981" data-id="element-684" data-mp="29i8lcinnoa5p" />
                                <circle cx="2" cy="2" r="1.5" fill="rgba(255,255,255,0.45)" data-id="element-685" data-mp="29j1m92zxo5fx" />
                                <circle cx="6" cy="6" r="1.5" fill="rgba(255,255,255,0.45)" data-id="element-686" data-mp="24i424kvd51bs" />
                              </pattern>
                              <pattern id={`pattern-crosshatch-${module.module}`} patternUnits="userSpaceOnUse" width="8" height="8" data-id="element-687" data-mp="ljfxbsaaqitq">
                                <rect width="8" height="8" fill="#DB3725" data-id="element-688" data-mp="qjakpluv4qa1" />
                                <path d="M0,0 l8,8 M8,0 l-8,8" stroke="rgba(255,255,255,0.35)" strokeWidth="2" data-id="element-689" data-mp="2tq2j56j9x1t" />
                              </pattern>
                              <pattern id={`pattern-horizontal-${module.module}`} patternUnits="userSpaceOnUse" width="8" height="8" data-id="element-690" data-mp="7t2olnpkz03c">
                                <rect width="8" height="8" fill="#F59E0B" data-id="element-691" data-mp="3b7ymxpywjhiw" />
                                <line x1="0" y1="2" x2="8" y2="2" stroke="rgba(255,255,255,0.4)" strokeWidth="2" data-id="element-692" data-mp="2ek8zephk7d0b" />
                                <line x1="0" y1="6" x2="8" y2="6" stroke="rgba(255,255,255,0.4)" strokeWidth="2" data-id="element-693" data-mp="2x9fbvurrf8do" />
                              </pattern>
                            </defs>
                          </svg>
                          {/* Bar */}
                          <div className="h-10 sm:h-12 flex rounded-lg overflow-hidden shadow-sm" data-id="element-694" data-mp="1gu4aiqmxyn2h">
                            <div className="flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90 overflow-hidden" style={{
                      width: `${module.confidentCorrect}%`,
                      backgroundColor: '#007558',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='8' fill='%23007558'/%3E%3Cpath d='M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4' stroke='rgba(255,255,255,0.35)' stroke-width='2.5'/%3E%3C/svg%3E")`
                    }} title={`Confident & Correct: ${module.confidentCorrect}%`} data-id="element-695" data-mp="19bmx49by2o48">
                              <span className="whitespace-nowrap hidden sm:inline rounded px-1.5 py-0.5 text-white font-bold text-xs" style={{
                        backgroundColor: '#005540'
                      }} data-id="element-696" data-mp="6xpcccu0vdsj">
                                {module.confidentCorrect}%
                              </span>
                            </div>
                            <div className="flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90 overflow-hidden" style={{
                      width: `${module.uncertainCorrect}%`,
                      backgroundColor: '#10B981'
                    }} title={`Uncertain & Correct: ${module.uncertainCorrect}%`} data-id="element-697" data-mp="3vi9a7yd5sgz4">
                              <span className="whitespace-nowrap hidden sm:inline rounded px-1.5 py-0.5 text-white font-bold text-xs" style={{
                        backgroundColor: '#0a8a5e'
                      }} data-id="element-698" data-mp="2ys0awe45379s">
                                {module.uncertainCorrect}%
                              </span>
                            </div>
                            <div className="flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90 overflow-hidden" style={{
                      width: `${module.confidentIncorrect}%`,
                      backgroundColor: '#DB3725',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='8' fill='%23DB3725'/%3E%3Cpath d='M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4' stroke='rgba(255,255,255,0.35)' stroke-width='2.5'/%3E%3C/svg%3E")`
                    }} title={`Confident & Incorrect: ${module.confidentIncorrect}%`} data-id="element-699" data-mp="2t3vish9zzhcb">
                              <span className="whitespace-nowrap hidden sm:inline rounded px-1.5 py-0.5 text-white font-bold text-xs" style={{
                        backgroundColor: '#b02d1e'
                      }} data-id="element-700" data-mp="w3pfjz4lw6r8">
                                {module.confidentIncorrect}%
                              </span>
                            </div>
                            <div className="flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90 overflow-hidden" style={{
                      width: `${module.uncertainIncorrect}%`,
                      backgroundColor: '#F87171'
                    }} title={`Uncertain & Incorrect: ${module.uncertainIncorrect}%`} data-id="element-701" data-mp="dbmxqkg8akfe">
                              <span className="whitespace-nowrap hidden sm:inline rounded px-1.5 py-0.5 text-white font-bold text-xs" style={{
                        backgroundColor: '#c44a4a'
                      }} data-id="element-702" data-mp="3e9qkqvjss68n">
                                {module.uncertainIncorrect}%
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Legend Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 sm:gap-y-5" data-id="element-703" data-mp="2qict6rth0eoq">
                          {/* Confident & Correct */}
                          <div className="flex items-center gap-3" data-id="element-704" data-mp="2gl834gwi7z2n">
                            <span className="text-sm font-bold text-[#007558] flex-shrink-0 w-10 text-right tabular-nums" data-id="element-705" data-mp="2ckef7nj2ak9r">
                              {module.confidentCorrect}%
                            </span>
                            <div className="w-6 h-6 rounded flex-shrink-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='8' fill='%23007558'/%3E%3Cpath d='M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4' stroke='rgba(255,255,255,0.35)' stroke-width='2.5'/%3E%3C/svg%3E")`
                    }} data-id="element-706" data-mp="7ld5ha4n3k8j"></div>
                            <div className="flex-1 min-w-0" data-id="element-707" data-mp="2o4ubthwsggs">
                              <span className="text-xs font-semibold text-gray-800" data-id="element-708" data-mp="3u1heewfxiicv">
                                Confident & Correct
                              </span>
                              <p className="text-[11px] text-gray-500 leading-tight" data-id="element-709" data-mp="36a286sl17gwc">
                                True mastery
                              </p>
                            </div>
                          </div>

                          {/* Uncertain & Correct */}
                          <div className="flex items-center gap-3" data-id="element-710" data-mp="2wzpn74h6y8pq">
                            <span className="text-sm font-bold text-[#10B981] flex-shrink-0 w-10 text-right tabular-nums" data-id="element-711" data-mp="2ia1l4116r60y">
                              {module.uncertainCorrect}%
                            </span>
                            <div className="w-6 h-6 rounded flex-shrink-0" style={{
                      backgroundColor: '#10B981'
                    }} data-id="element-712" data-mp="3axxf2xzdeju1"></div>
                            <div className="flex-1 min-w-0" data-id="element-713" data-mp="35x9qeiym6f2z">
                              <span className="text-xs font-semibold text-gray-800" data-id="element-714" data-mp="1pk1d3tbe1ctv">
                                Uncertain & Correct
                              </span>
                              <p className="text-[11px] text-gray-500 leading-tight" data-id="element-715" data-mp="1ukfkd9560iqu">
                                Lucky guesses
                              </p>
                            </div>
                          </div>

                          {/* Confident & Incorrect */}
                          <div className="flex items-center gap-3" data-id="element-716" data-mp="3nefa51s8h4p3">
                            <span className="text-sm font-bold text-[#DB3725] flex-shrink-0 w-10 text-right tabular-nums" data-id="element-717" data-mp="3ihotodcio735">
                              {module.confidentIncorrect}%
                            </span>
                            <div className="w-6 h-6 rounded flex-shrink-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='8' fill='%23DB3725'/%3E%3Cpath d='M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4' stroke='rgba(255,255,255,0.35)' stroke-width='2.5'/%3E%3C/svg%3E")`
                    }} data-id="element-718" data-mp="39ffm9wcjottz"></div>
                            <div className="flex-1 min-w-0" data-id="element-719" data-mp="159xab0x5tvuo">
                              <span className="text-xs font-semibold text-gray-800" data-id="element-720" data-mp="1a8iaj0cewfj4">
                                Confident & Incorrect
                              </span>
                              <p className="text-[11px] text-gray-500 leading-tight" data-id="element-721" data-mp="1jytsvrwuer9j">
                                Confident errors
                              </p>
                            </div>
                          </div>

                          {/* Uncertain & Incorrect */}
                          <div className="flex items-center gap-3" data-id="element-722" data-mp="w9imnuwq44o0">
                            <span className="text-sm font-bold text-[#F87171] flex-shrink-0 w-10 text-right tabular-nums" data-id="element-723" data-mp="w87j9w76wyxf">
                              {module.uncertainIncorrect}%
                            </span>
                            <div className="w-6 h-6 rounded flex-shrink-0" style={{
                      backgroundColor: '#F87171'
                    }} data-id="element-724" data-mp="27r0jeafw0411"></div>
                            <div className="flex-1 min-w-0" data-id="element-725" data-mp="26938rkmjc9s3">
                              <span className="text-xs font-semibold text-gray-800" data-id="element-726" data-mp="2k7t8l80qonk5">
                                Uncertain & Incorrect
                              </span>
                              <p className="text-[11px] text-gray-500 leading-tight" data-id="element-727" data-mp="2t7yeteu75fwq">
                                Needs practice
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>) : <div className="text-center py-8 text-gray-500" data-id="element-728" data-mp="2xzzl8k4ooceu">
                    No data available for the selected skill.
                  </div>}
              </div>
            </div>}
        </div>
      </div>

      {/* Performance Table Section */}
      <div data-phoebe-tour="analytics-table" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible mb-6" data-id="element-729" data-mp="3rjpdmssv4qla">
        <div className="bg-white px-4 sm:px-6 py-5 border-b border-gray-200 overflow-visible" data-id="element-730" data-mp="3vjg9hax8xwwo">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-731" data-mp="3hk89wgx68mry">
            <div data-id="element-732" data-mp="3mi00n7m2y2uk">
              <div className="flex items-center gap-2" data-id="element-733" data-mp="3mlnu66riyp9a">
                <h4 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2" data-id="element-734" data-mp="37tlyz00egkfl">
                  <Award size={22} className="text-[#30718D]" data-id="element-735" data-mp="3cmf2xlo1noqm" />
                  Performance Table
                </h4>
                <AIInsightPopover align="left" headline="A small group is driving most of the score gap" insightBeforeMetric="There are" highlightedMetric="3 learners" insightAfterMetric=" still below the 70% pass threshold." explanation="Supporting this group first would lift the overall average faster than a broad intervention." insightId={11} data-id="element-736" data-mp="2yvjsw61t71lg" />
              </div>
              <p className="text-sm text-gray-500 ml-[26px]" data-id="element-737" data-mp="25y9532p4ismx">
                Learner performance by selected metric
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3" data-id="element-738" data-mp="1w25ur9k4bnow">
              {/* Learner search dropdown */}
              <div className="relative" ref={performanceLearnerDropdownRef} data-id="element-739" data-mp="2tn281j45y0r1">
                <button onClick={() => {
                setIsPerformanceLearnerDropdownOpen(!isPerformanceLearnerDropdownOpen);
                setPerformanceLearnerSearch('');
              }} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[220px]" data-id="element-740" data-mp="2ord1gvtq7jl8">
                  <UsersRound size={16} className="text-[#30718D]" data-id="element-741" data-mp="3f1dinm6kn0mk" />
                  <span className="flex-1 text-left truncate" data-id="element-742" data-mp="3jym2rsba5eqg">
                    {performanceLearnerFilter}
                  </span>
                  <ChevronDown size={16} className={`transition-transform text-gray-500 ${isPerformanceLearnerDropdownOpen ? 'rotate-180' : ''}`} data-id="element-743" data-mp="3oxgn4yowbbco" />
                </button>

                {isPerformanceLearnerDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full sm:w-[280px] bg-white border border-gray-200 rounded-lg shadow-lg z-20" data-id="element-744" data-mp="6j7kkvae9gsz">
                    <div className="p-2 border-b border-gray-100" data-id="element-745" data-mp="bhiypi7q2dh0">
                      <div className="relative" data-id="element-746" data-mp="ghwwm7fm071b">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-747" data-mp="2daypqg7q1uci" />
                        <input type="text" autoFocus value={performanceLearnerSearch} onChange={e => setPerformanceLearnerSearch(e.target.value)} placeholder="Search learners..." className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D]/40 focus:border-[#30718D]" data-id="element-748" data-mp="o1fv7m1c68v0" />
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1" data-id="element-749" data-mp="f2b1ct0pz3p6">
                      {(() => {
                    const uniqueNames = Array.from(new Set(performanceTableData.map(r => r.name))).sort((a, b) => a.localeCompare(b));
                    const options = ['All Learners', ...uniqueNames];
                    const q = performanceLearnerSearch.trim().toLowerCase();
                    const filtered = q ? options.filter(n => n.toLowerCase().includes(q)) : options;
                    if (filtered.length === 0) {
                      return <div className="px-4 py-3 text-sm text-gray-500" data-id="element-750" data-mp="1o2t679jjiq50">
                              No learners found
                            </div>;
                    }
                    return filtered.map(name => <button key={name} onClick={() => {
                      setPerformanceLearnerFilter(name);
                      setIsPerformanceLearnerDropdownOpen(false);
                      setPerformanceLearnerSearch('');
                      setPerformancePage(1);
                    }} className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm ${performanceLearnerFilter === name ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-751" data-mp="22zah5znti2r1">
                            {name}
                          </button>);
                  })()}
                    </div>
                  </div>}
              </div>

              <div className="relative" ref={leaderboardMetricDropdownRef} data-id="element-752" data-mp="lrad5yj6otd9">
                <button onClick={() => setIsLeaderboardMetricDropdownOpen(!isLeaderboardMetricDropdownOpen)} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[200px]" data-id="element-753" data-mp="qok1tbyr1101">
                  <BarChart3 size={16} className="text-[#30718D]" data-id="element-754" data-mp="19i61umscf8ga" />
                  <span className="flex-1 text-left" data-id="element-755" data-mp="2u572bdlb778e">
                    {selectedLeaderboardMetric}
                  </span>
                  <ChevronDown size={16} className={`transition-transform text-gray-500 ${isLeaderboardMetricDropdownOpen ? 'rotate-180' : ''}`} data-id="element-756" data-mp="2p5azvis9fnuy" />
                </button>

                {isLeaderboardMetricDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-757" data-mp="2b85tfhomwmz6">
                    {['Average Score', 'Total Assessments', 'Completion Rate', 'Mastery Level'].map(metric => <button key={metric} onClick={() => {
                  setSelectedLeaderboardMetric(metric);
                  setIsLeaderboardMetricDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedLeaderboardMetric === metric ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-758" data-mp="3vmpqhw81zg8k">
                        {metric}
                      </button>)}
                  </div>}
              </div>

              <div className="relative" ref={leaderboardSkillDropdownRef} data-id="element-759" data-mp="2aj20r42s1xpn">
                <button onClick={() => setIsLeaderboardSkillDropdownOpen(!isLeaderboardSkillDropdownOpen)} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[200px]" data-id="element-760" data-mp="2ah7ijxiw4jp9">
                  <Target size={16} className="text-[#30718D]" data-id="element-761" data-mp="1jcbmloc68kga" />
                  <span className="flex-1 text-left" data-id="element-762" data-mp="1ecyzd2he1bd6">
                    {selectedLeaderboardSkill}
                  </span>
                  <ChevronDown size={16} className={`transition-transform text-gray-500 ${isLeaderboardSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-763" data-mp="1aavj80jrxilk" />
                </button>

                {isLeaderboardSkillDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-764" data-mp="vmr1ozwgjd4d">
                    {skillOptions.map(skill => <button key={skill} onClick={() => {
                  setSelectedLeaderboardSkill(skill);
                  setIsLeaderboardSkillDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedLeaderboardSkill === skill ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-765" data-mp="ql1r1ld5d1gt">
                        {skill}
                      </button>)}
                  </div>}
              </div>
              <ResetFiltersButton isActive={performanceLearnerFilter !== 'All Learners' || selectedLeaderboardMetric !== 'Average Score' || selectedLeaderboardSkill !== 'All Skills Combined'} onReset={() => {
              setPerformanceLearnerFilter('All Learners');
              setPerformanceLearnerSearch('');
              setSelectedLeaderboardMetric('Average Score');
              setSelectedLeaderboardSkill('All Skills Combined');
              setIsPerformanceLearnerDropdownOpen(false);
              setIsLeaderboardMetricDropdownOpen(false);
              setIsLeaderboardSkillDropdownOpen(false);
              setPerformancePage(1);
            }} data-id="element-766" data-mp="27x308xjuupmq" />
            </div>
          </div>
        </div>

        {/* Performance Table List */}
        {(() => {
        const parsePct = (v: string | number) => {
          if (typeof v === 'number') return v;
          const cleaned = String(v).replace('%', '').replace('+', '').trim();
          const n = parseFloat(cleaned);
          return isNaN(n) ? 0 : n;
        };
        const learnerFilteredPerf = performanceLearnerFilter === 'All Learners' ? performanceTableData : performanceTableData.filter(r => r.name === performanceLearnerFilter);
        const sortedPerf = [...learnerFilteredPerf].sort((a, b) => {
          const key = performanceSortKey;
          const valA = a[key];
          const valB = b[key];
          if (key === 'name') {
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            return performanceSortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
          }
          const numA = parsePct(valA as any);
          const numB = parsePct(valB as any);
          return performanceSortDir === 'asc' ? numA - numB : numB - numA;
        });
        const totalPages = Math.max(1, Math.ceil(sortedPerf.length / performancePerPage));
        const safePage = Math.min(performancePage, totalPages);
        const paginated = sortedPerf.slice((safePage - 1) * performancePerPage, safePage * performancePerPage);
        const isActive = (key: PerformanceTableSortKey) => performanceSortKey === key;
        const SortArrow = ({
          columnKey
        }: {
          columnKey: PerformanceTableSortKey;
        }) => {
          if (!isActive(columnKey)) {
            return <ChevronsUpDown size={14} className="text-gray-400" data-id="element-767" data-mp="3cycbqabjsg7j" />;
          }
          return performanceSortDir === 'asc' ? <ChevronUp size={14} className="text-[#30718D]" data-id="element-768" data-mp="3800zxu1ozvmo" /> : <ChevronDown size={14} className="text-[#30718D]" data-id="element-769" data-mp="330errz0q15kq" />;
        };
        const headerBtnClass = (columnKey: PerformanceTableSortKey) => `flex items-center gap-1 text-left text-sm font-medium transition-colors ${isActive(columnKey) ? 'text-[#30718D]' : 'text-gray-700 hover:text-gray-900'}`;
        const cellActiveClass = (columnKey: PerformanceTableSortKey) => isActive(columnKey) ? 'bg-[#30718D]/5' : '';
        const headerActiveClass = (columnKey: PerformanceTableSortKey) => isActive(columnKey) ? 'bg-[#30718D]/10' : '';
        return <div className="overflow-x-auto" data-id="element-770" data-mp="igzcuhgmafne">
              <table className="w-full text-sm" data-id="element-771" data-mp="ifxrnk5w0cry">
                <thead data-id="element-772" data-mp="3mzliiszmrwyy">
                  <tr className="border-b border-gray-200 bg-gray-50" data-id="element-773" data-mp="3myaecs29wn7b">
                    <th className="text-left px-4 sm:px-6 py-3 whitespace-nowrap w-12" data-id="element-774" data-mp="3rqtspr8sbmd6">
                      <span className="text-sm font-medium text-gray-700" data-id="element-775" data-mp="3dzoumtl2f24y">
                        #
                      </span>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('name')}`} data-id="element-776" data-mp="3iw5lhvjnfulq">
                      <button onClick={() => handlePerformanceSort('name')} className={headerBtnClass('name')} data-id="element-777" data-mp="eji7cd9b7q5v">
                        Learner Name
                        <SortArrow columnKey="name" data-id="element-778" data-mp="5lgckfg65tr1" />
                      </button>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('finalScore')}`} data-id="element-779" data-mp="2lcuf8b33usyc">
                      <button onClick={() => handlePerformanceSort('finalScore')} className={headerBtnClass('finalScore')} data-id="element-780" data-mp="2io8m5tfg8ics">
                        Final Score
                        <SortArrow columnKey="finalScore" data-id="element-781" data-mp="2mrnbxbt6wd3f" />
                      </button>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('improvement')}`} data-id="element-782" data-mp="1z1knr6sebra3">
                      <button onClick={() => handlePerformanceSort('improvement')} className={headerBtnClass('improvement')} data-id="element-783" data-mp="28rvt8xrqa8j3">
                        Improvement Rate
                        <SortArrow columnKey="improvement" data-id="element-784" data-mp="1gajhrysuyvh1" />
                      </button>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 whitespace-nowrap transition-colors ${headerActiveClass('attempts')}`} data-id="element-785" data-mp="1bbypqmnrfi3o">
                      <button onClick={() => handlePerformanceSort('attempts')} className={headerBtnClass('attempts')} data-id="element-786" data-mp="4kps7jpqvklj">
                        Attempts
                        <SortArrow columnKey="attempts" data-id="element-787" data-mp="3rs1ivfq97we1" />
                      </button>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 max-w-[180px] transition-colors ${headerActiveClass('confidentCorrect')}`} data-id="element-788" data-mp="s9903y4vtvd3">
                      <button onClick={() => handlePerformanceSort('confidentCorrect')} className={`${headerBtnClass('confidentCorrect')} items-start text-left`} data-id="element-789" data-mp="j93t3p3loyzg">
                        <span className="leading-snug" data-id="element-790" data-mp="2z103bdinfytz">
                          Confident & Correct Rate
                        </span>
                        <SortArrow columnKey="confidentCorrect" data-id="element-791" data-mp="1ehxlg189fuxs" />
                      </button>
                    </th>
                    <th className={`text-left px-4 sm:px-6 py-3 max-w-[180px] transition-colors ${headerActiveClass('confidentIncorrect')}`} data-id="element-792" data-mp="1nkp009wfn6tn">
                      <button onClick={() => handlePerformanceSort('confidentIncorrect')} className={`${headerBtnClass('confidentIncorrect')} items-start text-left`} data-id="element-793" data-mp="1xa8llnbj5es7">
                        <span className="leading-snug" data-id="element-794" data-mp="276le4hvc9mld">
                          Confident & Incorrect Rate
                        </span>
                        <SortArrow columnKey="confidentIncorrect" data-id="element-795" data-mp="cv6mtwmojf3i" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-796" data-mp="1k6w8isd3prx4">
                  {paginated.map((row, idx) => {
                const rank = (safePage - 1) * performancePerPage + idx + 1;
                return <tr key={`${row.name}-${idx}`} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors" data-id="element-797" data-mp="1f9xd86hpbexc" data-mpi={idx}>
                        <td className="px-4 sm:px-6 py-4 text-gray-400 tabular-nums align-middle" data-id="element-798" data-mp="235s2q3oym6ay">
                          {rank}
                        </td>
                        <td className={`px-4 sm:px-6 py-4 font-medium text-gray-900 align-middle whitespace-nowrap transition-colors ${cellActiveClass('name')}`} data-id="element-799" data-mp="1y65nxpthgi01">
                          {row.name}
                        </td>
                        <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('finalScore')}`} data-id="element-800" data-mp="2lxb3e3vih89t">
                          {row.finalScore}
                        </td>
                        <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('improvement')}`} data-id="element-801" data-mp="35h086eesgk69">
                          {row.improvement}
                        </td>
                        <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('attempts')}`} data-id="element-802" data-mp="2hs76007kpg37">
                          {row.attempts}
                        </td>
                        <td className={`px-4 sm:px-6 py-4 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('confidentCorrect')}`} data-id="element-803" data-mp="2csunfrnqiv6f">
                          <span className="text-[#30718D] font-medium" data-id="element-804" data-mp="23ro295a54w6u">
                            {row.confidentCorrect}
                          </span>
                        </td>
                        <td className={`px-4 sm:px-6 py-4 text-gray-700 align-middle tabular-nums whitespace-nowrap transition-colors ${cellActiveClass('confidentIncorrect')}`} data-id="element-805" data-mp="1qseyv32m56ma">
                          {row.confidentIncorrect}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>

              {/* Pagination */}
              {sortedPerf.length > 0 && <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-200" data-id="element-806" data-mp="132t02gq5ktet">
                  <p className="text-sm text-gray-600" data-id="element-807" data-mp="z2ivn8i0eb22">
                    Showing {(safePage - 1) * performancePerPage + 1}–
                    {Math.min(safePage * performancePerPage, sortedPerf.length)}{' '}
                    of {sortedPerf.length}
                  </p>
                  <div className="flex items-center gap-2" data-id="element-808" data-mp="f891h6d060qb">
                    <span className="text-sm text-gray-600" data-id="element-809" data-mp="f7r1pzr4t4dn">Page</span>
                    <div className="relative" ref={performancePageDropdownRef} data-id="element-810" data-mp="k5std92mic0q">
                      <button type="button" onClick={() => setIsPerformancePageDropdownOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isPerformancePageDropdownOpen} aria-label="Select page" className={`flex min-w-14 items-center justify-between rounded-md border bg-white py-1 pl-2 pr-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D]/20 ${isPerformancePageDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20 text-[#30718D]' : 'border-gray-300 text-gray-700'}`} data-id="element-811" data-mp="oxk90ok8qxc3">
                        <span data-id="element-812" data-mp="1lm8hat65n68b">{safePage}</span>
                        <ChevronDown size={14} aria-hidden="true" className={`ml-2 text-gray-400 transition-transform ${isPerformancePageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-813" data-mp="3hqoh6lxnnifs" />
                      </button>
                      {isPerformancePageDropdownOpen && <div role="listbox" aria-label="Select page" className="absolute bottom-full z-30 mb-1 max-h-60 min-w-full overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-814" data-mp="3mohpplmfkoj8">
                          {Array.from({
                    length: totalPages
                  }, (_, i) => i + 1).map(page => <button key={page} type="button" role="option" aria-selected={safePage === page} onClick={() => {
                    setPerformancePage(page);
                    setIsPerformancePageDropdownOpen(false);
                  }} className={`flex w-full items-center border-b border-gray-100 px-3 py-2 text-left text-sm transition-colors last:border-b-0 ${safePage === page ? 'bg-[#30718D]/10 font-medium text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-815" data-mp="2bama6brm6drh">
                              {page}
                            </button>)}
                        </div>}
                    </div>
                    <span className="text-sm text-gray-600" data-id="element-816" data-mp="3l7kfdb8nar0o">
                      of {totalPages}
                    </span>
                  </div>
                </div>}
            </div>;
      })()}
      </div>
      {/* End Performance Table Section */}
    </>;
}
function IndividualView({
  learner,
  modulePerformance,
  actionItems,
  COLORS,
  selectedSkill,
  setSelectedSkill,
  isSkillDropdownOpen,
  setIsSkillDropdownOpen,
  skillDropdownRef,
  skillOptions,
  selectedLearner,
  setSelectedLearner,
  selectedLearnerData,
  isLearnerDropdownOpen,
  setIsLearnerDropdownOpen,
  learnerDropdownRef
}) {
  // Filter action items for this specific learner (only non-macro items have learner property)
  const learnerActions = actionItems.filter(item => item.learner && (item.learner.name === learner.name || item.learner.email === learner.email));
  // Filter module performance based on selected skill
  const filteredModulePerformance = selectedSkill === 'All Skills Combined' ? modulePerformance : modulePerformance.filter(m => m.module === selectedSkill);
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  // Get recipient name for modals
  const getRecipientName = (item: any) => {
    if (item.learner) return item.learner.name;
    return 'Team';
  };
  const getRecipientFirstName = (item: any) => {
    if (item.learner) return item.learner.name.split(' ')[0];
    return 'Team';
  };
  // Learner-specific skill data for badges, certificates, assessments, scores, and dates
  const learnerSkillData = {
    // Linda Taylor (id: 1)
    1: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 2,
        avgScore: 92,
        lastAttempt: '2024-01-12'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 85,
        lastAttempt: '2024-01-10'
      },
      'JavaScript Basics': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 78,
        lastAttempt: '2024-01-15'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 88,
        lastAttempt: '2024-01-08'
      }
    },
    // John Smith (id: 2)
    2: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 75,
        lastAttempt: '2024-01-11'
      },
      'CSS Fundamentals': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 62,
        lastAttempt: '2024-01-13'
      },
      'JavaScript Basics': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 58,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 70,
        lastAttempt: '2024-01-09'
      }
    },
    // Sarah Johnson (id: 3)
    3: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 96,
        lastAttempt: '2024-01-10'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 94,
        lastAttempt: '2024-01-12'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 95,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 97,
        lastAttempt: '2024-01-16'
      }
    },
    // Michael Chen (id: 4)
    4: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 82,
        lastAttempt: '2024-01-08'
      },
      'CSS Fundamentals': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 65,
        lastAttempt: '2024-01-13'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 80,
        lastAttempt: '2024-01-11'
      },
      'Responsive Design': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 72,
        lastAttempt: '2024-01-06'
      }
    },
    // Emily Davis (id: 5)
    5: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 2,
        avgScore: 88,
        lastAttempt: '2024-01-15'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 90,
        lastAttempt: '2024-01-12'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 85,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 91,
        lastAttempt: '2024-01-10'
      }
    }
  };
  // Get skill-specific data for current learner
  const currentLearnerSkillData = learnerSkillData[learner.id] || learnerSkillData[1];
  const currentSkillData = selectedSkill !== 'All Skills Combined' ? currentLearnerSkillData[selectedSkill] : null;
  // Learner-specific progress data for charts - different data for each learner
  const learnerProgressData = {
    // Linda Taylor (id: 1) - Strong performer with steady improvement
    1: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }, {
          attempt: 'Attempt 2',
          score: 85
        }, {
          attempt: 'Attempt 3',
          score: 92
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }, {
          attempt: 'Attempt 2',
          mastery: 78
        }, {
          attempt: 'Attempt 3',
          mastery: 88
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 85
        }, {
          attempt: 'Attempt 2',
          score: 92
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }, {
          attempt: 'Attempt 2',
          mastery: 95
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 85
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 62
        }, {
          attempt: 'Attempt 2',
          score: 70
        }, {
          attempt: 'Attempt 3',
          score: 78
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 55
        }, {
          attempt: 'Attempt 2',
          mastery: 65
        }, {
          attempt: 'Attempt 3',
          mastery: 75
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 90
        }]
      }
    },
    // John Smith (id: 2) - Struggling learner with declining scores in some areas
    2: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 68
        }, {
          attempt: 'Attempt 3',
          score: 72
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 55
        }, {
          attempt: 'Attempt 2',
          mastery: 60
        }, {
          attempt: 'Attempt 3',
          mastery: 65
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 68
        }, {
          attempt: 'Attempt 2',
          score: 75
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 62
        }, {
          attempt: 'Attempt 2',
          mastery: 72
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 55
        }, {
          attempt: 'Attempt 2',
          score: 62
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 48
        }, {
          attempt: 'Attempt 2',
          mastery: 58
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 68
        }, {
          attempt: 'Attempt 2',
          score: 65
        }, {
          attempt: 'Attempt 3',
          score: 58
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 55
        }, {
          attempt: 'Attempt 3',
          mastery: 50
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 70
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 65
        }]
      }
    },
    // Sarah Johnson (id: 3) - High performer, first-attempt success
    3: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 95
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 94
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 96
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 95
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 94
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 92
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 95
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 93
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 97
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 96
        }]
      }
    },
    // Michael Chen (id: 4) - Mixed performance, struggles with CSS
    4: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 70
        }, {
          attempt: 'Attempt 2',
          score: 75
        }, {
          attempt: 'Attempt 3',
          score: 78
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 62
        }, {
          attempt: 'Attempt 2',
          mastery: 68
        }, {
          attempt: 'Attempt 3',
          mastery: 72
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 75
        }, {
          attempt: 'Attempt 2',
          score: 82
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 70
        }, {
          attempt: 'Attempt 2',
          mastery: 80
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 52
        }, {
          attempt: 'Attempt 2',
          score: 58
        }, {
          attempt: 'Attempt 3',
          score: 65
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 45
        }, {
          attempt: 'Attempt 2',
          mastery: 52
        }, {
          attempt: 'Attempt 3',
          mastery: 60
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }, {
          attempt: 'Attempt 2',
          score: 80
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }, {
          attempt: 'Attempt 2',
          mastery: 78
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }]
      }
    },
    // Emily Davis (id: 5) - Good performer with significant improvement
    5: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 85
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 92
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 90
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 78
        }, {
          attempt: 'Attempt 2',
          score: 85
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 72
        }, {
          attempt: 'Attempt 2',
          mastery: 82
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 91
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 89
        }]
      }
    }
  };
  // Get the appropriate data for the selected learner and skill
  const currentLearnerProgressData = learnerProgressData[learner.id] || learnerProgressData[1];
  const currentSkillProgressData = currentLearnerProgressData[selectedSkill] || currentLearnerProgressData['All Skills Combined'];
  const scoreProgressData = currentSkillProgressData.score;
  const masteryProgressData = currentSkillProgressData.mastery;
  return <>
      {/* Learner Header */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 mb-6" data-id="element-817" data-mp="2wltjjghqvvhw">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-818" data-mp="2rni7r07w3ax1">
          <div className="flex items-center gap-4" data-id="element-819" data-mp="2z70zpvz42ehr">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#30718D] flex items-center justify-center text-white text-lg sm:text-xl font-bold shadow-sm" data-id="element-820" data-mp="2z56eshnuamdo">
              {learner.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div data-id="element-821" data-mp="2u86hav522ry6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-822" data-mp="2u9r2vv9e70jw">
                {learner.name}
              </h2>
              <p className="text-gray-600 text-sm" data-id="element-823" data-mp="3mw2dlyqpqhuf">{learner.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-id="element-824" data-mp="3956yw5tj96ix">
            {/* Badge - Conditional based on skill selection */}
            <div className="text-center px-3 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-825" data-mp="3d4pld6105pnv">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-826" data-mp="4omdaz5z9yz0">
                {currentSkillData ? 'Badge Earned' : 'Badges Earned'}
              </div>
              {currentSkillData ? <div className={`text-base sm:text-lg font-bold ${currentSkillData.badgeEarned ? 'text-[#007558]' : 'text-gray-400'}`} data-id="element-827" data-mp="jbbk7m2k7eff">
                  {currentSkillData.badgeEarned ? 'Yes' : 'No'}
                </div> : <div className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-828" data-mp="5d4uit7rkkqu">
                  12
                </div>}
            </div>

            {/* Certificate - Conditional based on skill selection */}
            <div className="text-center px-3 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-829" data-mp="3rreij17s7qr3">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-830" data-mp="3isje9uoasqqf">
                {currentSkillData ? 'Certificate Achieved' : 'Certificates Achieved'}
              </div>
              {currentSkillData ? <div className={`text-base sm:text-lg font-bold ${currentSkillData.certificateAchieved ? 'text-[#007558]' : 'text-gray-400'}`} data-id="element-831" data-mp="38vodt68gsg9i">
                  {currentSkillData.certificateAchieved ? 'Yes' : 'No'}
                </div> : <div className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-832" data-mp="38pxjqgq053r8">
                  3
                </div>}
            </div>

            {/* Total Assessments - Updates based on skill */}
            <div className="text-center px-3 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-833" data-mp="2g1i3xrz1qheu">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-834" data-mp="2kwxiyn8sijcu">
                Total Assessments
              </div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-835" data-mp="2uouy252x5vgu">
                {currentSkillData ? currentSkillData.assessments : learner.assessments}
              </div>
            </div>

            {/* Average Score - Updates based on skill */}
            <div className="text-center px-3 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-836" data-mp="3gf0n8rj7wbfu">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-837" data-mp="3cdx51ydp63bg">
                Average Score
              </div>
              <div className="text-xl sm:text-2xl font-bold text-[#30718D]" data-id="element-838" data-mp="3qe7wb2ix5jx5">
                {currentSkillData ? currentSkillData.avgScore : learner.avgScore}
                %
              </div>
            </div>

            {/* Last Attempt / Attempted Date - Updates based on skill */}
            <div className="text-center px-3 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-lg border border-gray-100 col-span-2 sm:col-span-1" data-id="element-839" data-mp="3vc123ugb8bww">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-840" data-mp="7zgvjwy7312w">
                {currentSkillData ? 'Attempted Date' : 'Last Attempt'}
              </div>
              <div className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-841" data-mp="mwpp8xaz0gxs">
                {currentSkillData ? currentSkillData.lastAttempt : learner.lastAttempt}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Learner Progress Section */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 mb-6" data-id="element-842" data-mp="rkz85q3tn38x">
        <div className="flex items-start justify-between mb-6" data-id="element-843" data-mp="wk3hcgqqx0z9">
          <div data-id="element-844" data-mp="wiioqwnzrs41">
            <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-845" data-mp="wm6fxp5ys2fl">
              Individual Learner Progress
            </h3>
            <p className="text-sm text-gray-600" data-id="element-846" data-mp="mlnp2afzflo6">
              Track attempt-by-attempt progress for {learner.name.split(' ')[0]}{' '}
              in {selectedSkill}
            </p>
          </div>
          {selectedSkill !== 'All Skills Combined' && <span className="px-3 py-1 bg-[#30718D]/10 text-[#30718D] rounded-full text-sm font-medium" data-id="element-847" data-mp="dtu4mcxc1qtb">
              Viewing: {selectedSkill}
            </span>}
        </div>

        {/* Progress Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-848" data-mp="3x8z7qheidoy">
          {/* Score Progress Chart */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-849" data-mp="30qklypwk2te2">
            <h4 className="text-base font-bold text-gray-900 text-center mb-6" data-id="element-850" data-mp="35qqe076qmhwo">
              Score Progress
            </h4>
            <ResponsiveContainer width="100%" height={400} data-id="element-851" data-mp="2vtc0afc78782">
              <LineChart data={scoreProgressData} margin={{
              bottom: 20
            }} data-id="element-852" data-mp="2vrr64qtsqqaq">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-853" data-mp="2mt5rv7vcxxfr" />
                <XAxis dataKey="attempt" label={{
                value: 'Attempts',
                position: 'insideBottom',
                offset: -10
              }} tick={{
                fontSize: 12
              }} data-id="element-854" data-mp="2mugwf9wmn97x" />
                <YAxis domain={[0, 100]} label={{
                value: 'Percentage (%)',
                angle: -90,
                position: 'insideLeft'
              }} tick={{
                fontSize: 12
              }} data-id="element-855" data-mp="2356f5rh8gg7p" />
                <Tooltip data-id="element-856" data-mp="1thu2iadura3w" />
                <Line type="monotone" dataKey="score" stroke="#30718D" strokeWidth={3} dot={{
                fill: '#30718D',
                r: 6
              }} activeDot={{
                r: 8
              }} data-id="element-857" data-mp="1tic4lnn74ijq" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Mastery Rate Progress Chart */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-858" data-mp="286gtxagr7sbh">
            <h4 className="text-base font-bold text-gray-900 text-center mb-6" data-id="element-859" data-mp="2m6rlkfpw1axp">
              Mastery Rate Progress
            </h4>
            <ResponsiveContainer width="100%" height={400} data-id="element-860" data-mp="2h8ga60jy2sdd">
              <LineChart data={masteryProgressData} margin={{
              bottom: 20
            }} data-id="element-861" data-mp="1eu0d4i938n6j">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-862" data-mp="1isg8b9fbp1v1" />
                <XAxis dataKey="attempt" label={{
                value: 'Attempts',
                position: 'insideBottom',
                offset: -10
              }} tick={{
                fontSize: 12
              }} data-id="element-863" data-mp="1nskmgssrdki3" />
                <YAxis domain={[0, 100]} label={{
                value: 'Percentage (%)',
                angle: -90,
                position: 'insideLeft'
              }} tick={{
                fontSize: 12
              }} data-id="element-864" data-mp="22kmjm53g1zef" />
                <Tooltip data-id="element-865" data-mp="h4x91flafy3m" />
                <Line type="monotone" dataKey="mastery" stroke="#007558" strokeWidth={3} dot={{
                fill: '#007558',
                r: 6
              }} activeDot={{
                r: 8
              }} data-id="element-866" data-mp="h68av9v6avs3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recommended Actions Section */}
      <AnimatePresence mode="wait" data-id="element-867" data-mp="sk9zyz0h5mhf">
        {learnerActions.length > 0 && <motion.div key={learner.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.3
      }} className="mb-6" data-id="element-868" data-mp="nmqmw2fijfr2"></motion.div>}
      </AnimatePresence>

      {/* Individual Module Performance */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-869" data-mp="r1iewv8f3x26">
        <div className="flex items-center justify-between mb-5" data-id="element-870" data-mp="vtjno76dn5ng">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-871" data-mp="vv32rugarann">
            {selectedSkill === 'All Skills Combined' ? 'Module Performance' : `${selectedSkill} Performance`}
          </h3>
          {selectedSkill !== 'All Skills Combined' && <span className="px-3 py-1 bg-[#30718D]/10 text-[#30718D] rounded-full text-sm font-medium" data-id="element-872" data-mp="19tughfr8yub9">
              Filtered
            </span>}
        </div>
        <div className="space-y-4" data-id="element-873" data-mp="10rbfz2axots6">
          {filteredModulePerformance.length > 0 ? filteredModulePerformance.map((module, index) => <div key={index} className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-874" data-mp="14vsvgpa9gkx5" data-mpi={index}>
                <div className="flex items-center justify-between mb-4" data-id="element-875" data-mp="3pl8jxdm0bxjm">
                  <div data-id="element-876" data-mp="3i0egdvv3mhx8">
                    <h4 className="font-semibold text-base text-gray-900" data-id="element-877" data-mp="3hzmw3h6yc6m9">
                      {module.module}
                    </h4>
                    <p className="text-sm text-gray-500 mt-0.5" data-id="element-878" data-mp="3d3o13ar4bqcv">
                      {module.attempts} attempt
                      {module.attempts !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-879" data-mp="3mxwnrhtgwp34">
                    <div className={`text-3xl font-bold ${module.score >= 85 ? 'text-[#007558]' : 'text-[#DB3725]'}`} data-id="element-880" data-mp="2zb82cm592ehr">
                      {module.score}%
                    </div>
                    <div className="flex items-center justify-end gap-1.5 mt-1" data-id="element-881" data-mp="391axoszug4j9">
                      {module.score >= 85 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#007558]/10 text-[#007558]" data-id="element-882" data-mp="2gjp2upafpikh">
                          <CheckCircle size={12} data-id="element-883" data-mp="2bk2qei2fk6cq" />
                          Passed
                        </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#DB3725]/10 text-[#DB3725]" data-id="element-884" data-mp="35abjqaupn3ey">
                          <AlertTriangle size={12} data-id="element-885" data-mp="3a2cq5g575zx2" />
                          Not Passed
                        </span>}
                    </div>
                    <div className="text-xs font-medium text-gray-500 mt-0.5" data-id="element-886" data-mp="2iadm06hwjqtc">
                      Highest Assessment Score
                    </div>
                  </div>
                </div>

                {/* Confidence Analysis */}
                <div data-id="element-887" data-mp="2r8z64630vdw8">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-888" data-mp="2rcwqev1c6bhp">
                    Answer Confidence Breakdown
                  </h5>

                  {/* Stacked Bar Visualization */}
                  <div className="mb-4" data-id="element-889" data-mp="6mp5kto64x3w">
                    <div className="h-8 flex rounded-lg overflow-hidden shadow-sm" data-id="element-890" data-mp="bimmp279a7hq">
                      {/* Confident + Correct */}
                      <div className="bg-[#007558] flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90" style={{
                  width: `${module.confidentCorrect}%`
                }} title={`Confident & Correct: ${module.confidentCorrect}%`} data-id="element-891" data-mp="1w0oreygzck64">
                        {module.confidentCorrect >= 10 && `${module.confidentCorrect}%`}
                      </div>

                      {/* Uncertain + Correct */}
                      <div className="bg-[#10B981] flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90" style={{
                  width: `${module.uncertainCorrect}%`
                }} title={`Uncertain & Correct: ${module.uncertainCorrect}%`} data-id="element-892" data-mp="13evlig0e0pf4">
                        {module.uncertainCorrect >= 10 && `${module.uncertainCorrect}%`}
                      </div>

                      {/* Confident + Incorrect */}
                      <div className="bg-[#DB3725] flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90" style={{
                  width: `${module.confidentIncorrect}%`
                }} title={`Confident & Incorrect: ${module.confidentIncorrect}%`} data-id="element-893" data-mp="1ss8n8zrun15z">
                        {module.confidentIncorrect >= 10 && `${module.confidentIncorrect}%`}
                      </div>

                      {/* Uncertain + Incorrect */}
                      <div className="bg-[#F59E0B] flex items-center justify-center text-white text-xs font-bold transition-all duration-500 hover:opacity-90" style={{
                  width: `${module.uncertainIncorrect}%`
                }} title={`Uncertain & Incorrect: ${module.uncertainIncorrect}%`} data-id="element-894" data-mp="38ajwlcj08swl">
                        {module.uncertainIncorrect >= 10 && `${module.uncertainIncorrect}%`}
                      </div>
                    </div>
                  </div>

                  {/* Legend Grid */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-895" data-mp="1bbush8xbnb16">
                    <div className="flex items-start gap-2" data-id="element-896" data-mp="1pcnj3iki5lz0">
                      <div className="w-4 h-4 rounded bg-[#007558] flex-shrink-0 mt-0.5" data-id="element-897" data-mp="1p9rp25brzhfu"></div>
                      <div className="flex-1 min-w-0" data-id="element-898" data-mp="1kayccf4bbugq">
                        <div className="flex items-center gap-2" data-id="element-899" data-mp="3r4jums6o6vts">
                          <span className="text-xs font-bold text-[#007558] min-w-[32px]" data-id="element-900" data-mp="3m4nqmsxz34e8">
                            {module.confidentCorrect}%
                          </span>
                          <span className="text-xs font-semibold text-gray-700" data-id="element-901" data-mp="2xifa50i79bl1">
                            Confident & Correct
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 ml-[40px]" data-id="element-902" data-mp="2nopzjtitk70w">
                          True mastery
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2" data-id="element-903" data-mp="2dqio1q7auh6a">
                      <div className="w-4 h-4 rounded bg-[#10B981] flex-shrink-0 mt-0.5" data-id="element-904" data-mp="29pytmue41rnu"></div>
                      <div className="flex-1 min-w-0" data-id="element-905" data-mp="24szwe2z5h4lf">
                        <div className="flex items-center gap-2" data-id="element-906" data-mp="1zsu20f1hx3zn">
                          <span className="text-xs font-bold text-[#10B981] min-w-[32px]" data-id="element-907" data-mp="1zuf02en0r529">
                            {module.uncertainCorrect}%
                          </span>
                          <span className="text-xs font-semibold text-gray-700" data-id="element-908" data-mp="3u7wkvtw1hntl">
                            Uncertain & Correct
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 ml-[40px]" data-id="element-909" data-mp="aj1hhxa2fbcg">
                          Lucky guesses
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2" data-id="element-910" data-mp="k8usaw2dvmfn">
                      <div className="w-4 h-4 rounded bg-[#DB3725] flex-shrink-0 mt-0.5" data-id="element-911" data-mp="3irjn1c6s74gf"></div>
                      <div className="flex-1 min-w-0" data-id="element-912" data-mp="3nqmrauext6nc">
                        <div className="flex items-center gap-2" data-id="element-913" data-mp="3nref3j2am032">
                          <span className="text-xs font-bold text-[#DB3725] min-w-[32px]" data-id="element-914" data-mp="3sp6eey59ooha">
                            {module.confidentIncorrect}%
                          </span>
                          <span className="text-xs font-semibold text-gray-700" data-id="element-915" data-mp="17zgyehstuwn0">
                            Confident & Incorrect
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 ml-[40px]" data-id="element-916" data-mp="391q0ksiifiph">
                          Confident errors
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2" data-id="element-917" data-mp="3i2ec9gkfl6z7">
                      <div className="w-4 h-4 rounded bg-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-918" data-mp="3i1uy8axwj2qg"></div>
                      <div className="flex-1 min-w-0" data-id="element-919" data-mp="3e1axyyi17j03">
                        <div className="flex items-center gap-2" data-id="element-920" data-mp="2qap2e6p7rr6z">
                          <span className="text-xs font-bold text-[#F59E0B] min-w-[32px]" data-id="element-921" data-mp="2q8ugoq64bv1u">
                            {module.uncertainIncorrect}%
                          </span>
                          <span className="text-xs font-semibold text-gray-700" data-id="element-922" data-mp="2z4tnsalkwaf4">
                            Uncertain & Incorrect
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 ml-[40px]" data-id="element-923" data-mp="26log6q9q1vjb">
                          Needs practice
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>) : <div className="text-center py-8 text-gray-500" data-id="element-924" data-mp="1x97etkqspvvj">
              No performance data available for {selectedSkill}.
            </div>}
        </div>
      </div>

      {/* Enhanced Mastery Heatmap */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-925" data-mp="1niw4nggiqqg7">
        <div className="mb-6" data-id="element-926" data-mp="2b8qdlmxpvuv6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-927" data-mp="2b6ckvkqieuqs">
            Knowledge Mastery Overview
          </h3>
          <p className="text-gray-600 text-sm" data-id="element-928" data-mp="21a9h6ag0eo1q">
            Visual representation of {learner.name.split(' ')[0]}'s proficiency
            {selectedSkill === 'All Skills Combined' ? ' across all modules' : ` in ${selectedSkill}`}
          </p>
        </div>

        {filteredModulePerformance.length > 0 ? <div className={`grid gap-4 mb-8 ${filteredModulePerformance.length === 1 ? 'grid-cols-1 max-w-xs' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`} data-id="element-929" data-mp="uuqege6p498z">
            {filteredModulePerformance.map((module, index) => {
          const masteryLevel = module.mastery >= 90 ? 'expert' : module.mastery >= 75 ? 'proficient' : module.mastery >= 60 ? 'developing' : 'needs-work';
          const colorConfig = {
            expert: {
              bg: 'from-[#007558] to-[#005a44]',
              border: 'border-[#007558]',
              text: 'text-white',
              icon: '★'
            },
            proficient: {
              bg: 'from-[#30718D] to-[#265a70]',
              border: 'border-[#30718D]',
              text: 'text-white',
              icon: '✓'
            },
            developing: {
              bg: 'from-[#F59E0B] to-[#D97706]',
              border: 'border-[#F59E0B]',
              text: 'text-white',
              icon: '↗'
            },
            'needs-work': {
              bg: 'from-[#DB3725] to-[#b82e1f]',
              border: 'border-[#DB3725]',
              text: 'text-white',
              icon: '!'
            }
          };
          const config = colorConfig[masteryLevel];
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            delay: index * 0.1,
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} whileHover={{
            scale: 1.05,
            y: -4,
            transition: {
              duration: 0.2
            }
          }} className="group relative cursor-pointer" data-id="element-930" data-mp="2z0xgwdykqiaf" data-mpi={index}>
                  <div className={`
                    relative overflow-hidden rounded-xl border-2 ${config.border}
                    bg-gradient-to-br ${config.bg}
                    shadow-md hover:shadow-xl
                    transition-all duration-300
                    aspect-square
                  `} data-id="element-931" data-mp="1fj9hj4gtydf0">
                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-932" data-mp="1y8zbjpcrqv5v" />

                    {/* Content */}
                    <div className="relative h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-933" data-mp="288ghm7vxnb2d">
                      {/* Attempts Badge */}
                      <motion.div className="absolute top-3 right-3" initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: index * 0.1 + 0.3,
                  type: 'spring'
                }} data-id="element-934" data-mp="2rrmdtsbg9axg">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1" data-id="element-935" data-mp="2cxrbl55sujv8">
                          <span className={`text-xs font-bold ${config.text}`} data-id="element-936" data-mp="1z6cqmssc08lj">
                            Attempts: {module.attempts}
                          </span>
                        </div>
                      </motion.div>

                      {/* Score */}
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: index * 0.1 + 0.2,
                  type: 'spring',
                  stiffness: 300
                }} className="mb-3" data-id="element-937" data-mp="1u5ygn5766okv">
                        <div className={`text-5xl font-black ${config.text} drop-shadow-lg`} data-id="element-938" data-mp="1qp3x1nxgs62x">
                          {module.mastery}%
                        </div>
                      </motion.div>

                      {/* Module Name */}
                      <div className={`text-sm font-bold ${config.text} drop-shadow-md leading-tight px-2`} data-id="element-939" data-mp="1cy8m863enewp">
                        {module.module}
                      </div>

                      {/* Progress Ring */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-16 h-1 bg-white/30 rounded-full overflow-hidden" data-id="element-940" data-mp="svcrq9834h0a">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${module.mastery}%`
                  }} transition={{
                    delay: index * 0.1 + 0.4,
                    duration: 0.8
                  }} className="h-full bg-white/90 rounded-full" data-id="element-941" data-mp="xtmjsayij1ho" />
                      </div>
                    </div>

                    {/* Hover Tooltip */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl p-6 flex flex-col justify-center bg-white shadow-xl" data-id="element-942" data-mp="ouj0b5vfok2n">
                      <div className="space-y-3" data-id="element-943" data-mp="3shsvg1ku5w4c">
                        <div className="text-base font-bold border-b border-gray-200 pb-2 text-gray-900" data-id="element-944" data-mp="3sghs22vayqdr">
                          {module.module}
                        </div>

                        <div className="space-y-2 text-sm" data-id="element-945" data-mp="3varm3ewemflg">
                          <div className="flex justify-between items-center" data-id="element-946" data-mp="3r9o3ikmz25gj">
                            <span className="text-gray-600" data-id="element-947" data-mp="3mbmkfzpjq652">
                              Overall Score:
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-948" data-mp="mvpopbehh9ds">
                              {module.score}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center" data-id="element-949" data-mp="dui601ogah4t">
                            <span className="text-gray-600" data-id="element-950" data-mp="8wzsrz3ehfr0">Mastery:</span>
                            <span className="font-bold text-[#007558]" data-id="element-951" data-mp="2ylsbht8cl1q3">
                              {module.mastery}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center" data-id="element-952" data-mp="2tp1xdbg3sg7b">
                            <span className="text-gray-600" data-id="element-953" data-mp="2jklioled1553">
                              Confident errors:
                            </span>
                            <span className="font-bold text-[#DB3725]" data-id="element-954" data-mp="2th80f1be3g2w">
                              {module.misinformation}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center pt-2 border-t border-gray-200" data-id="element-955" data-mp="329idqrhl01od">
                            <span className="text-gray-600" data-id="element-956" data-mp="377tnl27vmc6l">Attempts:</span>
                            <span className="font-bold text-[#30718D]" data-id="element-957" data-mp="37a63zaxzkehj">
                              {module.attempts}
                            </span>
                          </div>
                        </div>

                        <div className="pt-2" data-id="element-958" data-mp="1htojrr31q69x">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${masteryLevel === 'expert' ? 'bg-[#007558]/30 text-[#007558] border-[#007558]/50' : masteryLevel === 'proficient' ? 'bg-[#30718D]/30 text-[#30718D] border-[#30718D]/50' : masteryLevel === 'developing' ? 'bg-[#F59E0B]/30 text-[#F59E0B] border-[#F59E0B]/50' : 'bg-[#DB3725]/30 text-[#DB3725] border-[#DB3725]/50'}`} data-id="element-959" data-mp="1huzl7k90r1xv">
                            <span data-id="element-960" data-mp="1jpbsid6g7o7q">{config.icon}</span>
                            <span data-id="element-961" data-mp="1xqehoysqpemv">
                              {masteryLevel === 'expert' ? 'Expert Level' : masteryLevel === 'proficient' ? 'Proficient' : masteryLevel === 'developing' ? 'Developing' : 'Needs Focus'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>;
        })}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-962" data-mp="2v21oz1b83o6i">
            No performance data available for {selectedSkill}.
          </div>}

        {/* Enhanced Legend */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-963" data-mp="2m2wm5ismp8oj">
          <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide" data-id="element-964" data-mp="2r0y6e71sje1l">
            Mastery Levels
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-965" data-mp="2cba6995cilfg">
            <div className="flex items-center gap-3" data-id="element-966" data-mp="2h4v5f4iodlg8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#DB3725] to-[#b82e1f] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-967" data-mp="23cyidevv61qp">
                <span className="text-white font-bold text-lg" data-id="element-968" data-mp="27etl8ntg0n6l">!</span>
              </div>
              <div data-id="element-969" data-mp="1tftw7f20q69w">
                <div className="font-bold text-gray-900 text-sm" data-id="element-970" data-mp="1tglqmmjvpxyl">
                  Needs Work
                </div>
                <div className="text-xs text-gray-500" data-id="element-971" data-mp="1yfga7qpo7qjr">0-59%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-972" data-mp="2wpeo9c1zrmgv">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-973" data-mp="2wqpurjmtn8bo">
                <span className="text-white font-bold text-lg" data-id="element-974" data-mp="2mzv29xizfchs">↗</span>
              </div>
              <div data-id="element-975" data-mp="2i0ig7ezxq9g9">
                <div className="font-bold text-gray-900 text-sm" data-id="element-976" data-mp="2i0savfvxd4sa">
                  Developing
                </div>
                <div className="text-xs text-gray-500" data-id="element-977" data-mp="2d0e4016280vu">60-74%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-978" data-mp="290n2b2ezw0cj">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#30718D] to-[#265a70] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-979" data-mp="240r1fc1lgp17">
                <span className="text-white font-bold text-lg" data-id="element-980" data-mp="77yxwpqm76v6">✓</span>
              </div>
              <div data-id="element-981" data-mp="2ap7b6rhop5r">
                <div className="font-bold text-gray-900 text-sm" data-id="element-982" data-mp="27b6w42dyq0y">
                  Proficient
                </div>
                <div className="text-xs text-gray-500" data-id="element-983" data-mp="9r2irbo5dz6d">75-89%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-984" data-mp="jiq6p2lhwy33">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007558] to-[#005a44] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-985" data-mp="jk1d7a6bsjxw">
                <span className="text-white font-bold text-lg" data-id="element-986" data-mp="3mzovfpzvtanw">★</span>
              </div>
              <div data-id="element-987" data-mp="3ryi6zcvlyrlf">
                <div className="font-bold text-gray-900 text-sm" data-id="element-988" data-mp="3rxqdc7lkn3xs">Mastery</div>
                <div className="text-xs text-gray-500" data-id="element-989" data-mp="s8klgv650qp">90-100%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}
function MetricCard({
  icon,
  label,
  value,
  change,
  changeLabel,
  color
}) {
  const colorClasses = {
    blue: 'bg-[#30718D]/10 text-[#30718D]',
    green: 'bg-[#007558]/10 text-[#007558]',
    purple: 'bg-[#30718D]/10 text-[#30718D]',
    orange: 'bg-orange-100 text-orange-600'
  };
  const isPositive = change > 0;
  const isNeutral = change === 0;
  return <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-990" data-mp="38b1w9g45e1xu">
      <div className="flex items-center justify-between mb-2" data-id="element-991" data-mp="3mab8y5t5adx2">
        <span className="text-sm font-medium text-gray-600" data-id="element-992" data-mp="3m9icpyrtb6t6">{label}</span>
        {isPositive ? <TrendingUp size={20} className="text-green-600" data-id="element-993" data-mp="3hc00nzii0bgy" /> : isNeutral ? <Minus size={20} className="text-gray-400" data-id="element-994" data-mp="2tmfe76n4yw2x" /> : <TrendingUp size={20} className="text-red-600 rotate-180" data-id="element-995" data-mp="2pkkeg6kusqr9" />}
      </div>
      <div className="flex items-end justify-between" data-id="element-996" data-mp="33bz0kma2582j">
        <div className="text-3xl font-bold text-gray-900" data-id="element-997" data-mp="33cqm149xxpf3">{value}</div>
        <span className={`text-lg font-bold ${isPositive ? 'text-green-600' : isNeutral ? 'text-gray-500' : 'text-red-600'}`} data-id="element-998" data-mp="2yjnybcruha6t">
          {isPositive ? '+' : ''}
          {change}%
        </span>
      </div>
    </div>;
}
function SkillPerformanceHeader({
  selectedInsightSkill,
  setSelectedInsightSkill,
  isInsightSkillDropdownOpen,
  setIsInsightSkillDropdownOpen,
  insightSkillDropdownRef,
  skillOptions
}) {
  return <div className="bg-white px-4 sm:px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-id="element-999" data-mp="q6k5e4r1y3fk">
      <div data-id="element-1000" data-mp="hdgxsd338ksu">
        <h4 className="text-base font-bold text-gray-900 mb-0.5 flex items-center gap-2" data-id="element-1001" data-mp="hcxfgv9izuea">
          <BarChart3 size={22} className="text-[#30718D]" data-id="element-1002" data-mp="153bb0vrbkbdm" />
          Skill Performance
        </h4>
        <p className="text-sm text-gray-500 ml-[26px]" data-id="element-1003" data-mp="192adm9pkvw1w">
          {selectedInsightSkill === 'All Skills Combined' ? 'Detailed metrics for all skills' : `Detailed metrics for ${selectedInsightSkill}`}
        </p>
      </div>
      <div className="relative" ref={insightSkillDropdownRef} data-id="element-1004" data-mp="3fu1c5bsehbcg">
        <button onClick={() => setIsInsightSkillDropdownOpen(!isInsightSkillDropdownOpen)} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-bold text-gray-900 w-full sm:min-w-[200px]" data-id="element-1005" data-mp="3fsgkpv1du8it">
          <Target size={16} className="text-[#30718D]" data-id="element-1006" data-mp="6d5dmwb4x5ig" />
          <span className="flex-1 text-left" data-id="element-1007" data-mp="6dx1tm2ek0yp">{selectedInsightSkill}</span>
          <ChevronDown size={16} className={`transition-transform text-gray-500 ${isInsightSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1008" data-mp="1eu5qr4eh52v" />
        </button>

        {isInsightSkillDropdownOpen && <div className="absolute top-full mt-2 right-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-1009" data-mp="u384j93bc5fh">
            {skillOptions.map(skill => <button key={skill} onClick={() => {
          setSelectedInsightSkill(skill);
          setIsInsightSkillDropdownOpen(false);
        }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedInsightSkill === skill ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1010" data-mp="kdoi5tge5tfv">
                {skill}
              </button>)}
          </div>}
      </div>
    </div>;
}